import { CloseOutlined, CopyOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, DatePicker, Drawer, Input, Modal, Popover, Select, Space, Spin, Switch, Tooltip, message } from 'antd';
import { useBackdropClose } from '../../hooks/useBackdropClose';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useId, useRef, useState } from 'react';
import {
  CABINET_HISTORY_PAGE_SIZE,
  cabinetPurchaseStatusPartsFromOrder,
  formatCabinetHistoryAmount,
  formatCabinetHistoryDateTime,
  operationHistoryRowsFromBillingItems,
  orderTypeLabelRu,
  paymentStatusLabelRu,
  purchaseHistoryRowsFromBillingItems,
  tariffTariffKindFromCabinetName,
} from './cabinet-history-from-orders';
import { apiService } from '../../services/api';
import { normalizeClientCabinetEmailDisplay } from '../../utils/normalizeClientCabinetEmailDisplay';
import { openAppPathInNewTab } from '../../utils/openAppPathInNewTab';
import BronzeLevelHeaderBadge from './bronze-level-header-badge';
import goldLevelHeaderWordmark from './gold-level-header-wordmark.svg';
import silverLevelHeaderWordmark from './silver-level-header-wordmark.svg';
import './client-detail-drawer.css';

const TG_CHECKBOX_BOX_PATH =
  'M12.6667 0H3.33333C2.4496 0.00105857 1.60237 0.352588 0.97748 0.97748C0.352588 1.60237 0.00105857 2.4496 0 3.33333L0 12.6667C0.00105857 13.5504 0.352588 14.3976 0.97748 15.0225C1.60237 15.6474 2.4496 15.9989 3.33333 16H12.6667C13.5504 15.9989 14.3976 15.6474 15.0225 15.0225C15.6474 14.3976 15.9989 13.5504 16 12.6667V3.33333C15.9989 2.4496 15.6474 1.60237 15.0225 0.97748C14.3976 0.352588 13.5504 0.00105857 12.6667 0V0ZM14.6667 12.6667C14.6667 13.1971 14.456 13.7058 14.0809 14.0809C13.7058 14.456 13.1971 14.6667 12.6667 14.6667H3.33333C2.8029 14.6667 2.29419 14.456 1.91912 14.0809C1.54405 13.7058 1.33333 13.1971 1.33333 12.6667V3.33333C1.33333 2.8029 1.54405 2.29419 1.91912 1.91912C2.29419 1.54405 2.8029 1.33333 3.33333 1.33333H12.6667C13.1971 1.33333 13.7058 1.54405 14.0809 1.91912C14.456 2.29419 14.6667 2.8029 14.6667 3.33333V12.6667Z';

const TG_CHECKBOX_CHECK_PATH =
  'M6.22172 10.6127L3.60905 8.00003C3.48403 7.87505 3.31449 7.80484 3.13772 7.80484C2.96094 7.80484 2.7914 7.87505 2.66638 8.00003C2.5414 8.12505 2.47119 8.29459 2.47119 8.47136C2.47119 8.64814 2.5414 8.81768 2.66638 8.9427L5.27905 11.5554C5.40287 11.6792 5.54988 11.7775 5.71168 11.8445C5.87348 11.9116 6.04691 11.9461 6.22205 11.9461C6.39719 11.9461 6.57062 11.9116 6.73242 11.8445C6.89422 11.7775 7.04123 11.6792 7.16505 11.5554L13.333 5.38737C13.458 5.26235 13.5282 5.09281 13.5282 4.91604C13.5282 4.73926 13.458 4.56972 13.333 4.4447C13.208 4.31972 13.0385 4.24951 12.8617 4.24951C12.6849 4.24951 12.5154 4.31972 12.3904 4.4447L6.22172 10.6127Z';

/** Чекбокс уведомлений Telegram — макет Figma 16×16 */
const TgCheckboxIcon = ({ checked }) => (
  <svg
    className="client-detail-drawer__tg-checkbox-svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path d={TG_CHECKBOX_BOX_PATH} fill="#C2C2C2" />
    {checked ? <path d={TG_CHECKBOX_CHECK_PATH} fill="#5283F4" /> : null}
  </svg>
);

/** До этого ширины — макет мобильной панели (чипы, сетка шапки, узкая таблица) */
const CLIENT_DETAIL_MOBILE_MAX_PX = 639;

/** Десктоп: ширина до 1200px, иначе почти на всю ширину окна (шапка и тело занимают доступную область). */
const CLIENT_DETAIL_DRAWER_DESKTOP_WIDTH_CSS = 'min(1200px, calc(100vw - 12px))';

const LOGS_PAGE_SIZE = 50;

/** Фильтры журнала; значения отправляются в GET /api/clients/:id/activity-log */
const INITIAL_CLIENT_ACTIVITY_LOG_FILTERS = {
  dateFrom: '',
  dateTo: '',
  level: '',
  category: '',
  action: '',
  message: '',
  user: '',
};

const isoUtcRangeFromYmd = (fromYmd, toYmd) => {
  let dateFromIso = '';
  let dateToIso = '';
  if (fromYmd && typeof fromYmd === 'string') {
    const parts = fromYmd.split('-').map((x) => Number(x));
    if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
      const [y, m, d] = parts;
      dateFromIso = new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0)).toISOString();
    }
  }
  if (toYmd && typeof toYmd === 'string') {
    const parts = toYmd.split('-').map((x) => Number(x));
    if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
      const [y, m, d] = parts;
      dateToIso = new Date(Date.UTC(y, m - 1, d, 23, 59, 59, 999)).toISOString();
    }
  }
  return { dateFromIso, dateToIso };
};

const buildActivityLogRequestParams = (offset, filters) => {
  const o = Math.max(0, Number(offset) || 0);
  const f = filters || INITIAL_CLIENT_ACTIVITY_LOG_FILTERS;
  const params = { limit: LOGS_PAGE_SIZE, offset: o };

  const lvl = String(f.level || '').trim().toLowerCase();
  if (lvl) {
    params.level = lvl;
  }
  const cat = String(f.category || '').trim().toLowerCase();
  if (cat) {
    params.category = cat;
  }

  const { dateFromIso, dateToIso } = isoUtcRangeFromYmd(f.dateFrom, f.dateTo);
  if (dateFromIso) {
    params.date_from = dateFromIso;
  }
  if (dateToIso) {
    params.date_to = dateToIso;
  }

  const action = String(f.action || '').trim();
  if (action) {
    params.action_contains = action;
  }
  const ms = String(f.message || '').trim();
  if (ms) {
    params.message_contains = ms;
  }
  const usr = String(f.user || '').trim();
  if (usr) {
    params.username_contains = usr;
  }
  return params;
};

const countActiveActivityLogFilters = (f) => {
  if (!f) {
    return 0;
  }
  let n = 0;
  if ((f.dateFrom && String(f.dateFrom).trim() !== '') || (f.dateTo && String(f.dateTo).trim() !== '')) {
    n += 1;
  }
  if ((f.level || '').trim()) {
    n += 1;
  }
  if ((f.category || '').trim()) {
    n += 1;
  }
  if ((f.action || '').trim()) {
    n += 1;
  }
  if ((f.message || '').trim()) {
    n += 1;
  }
  if ((f.user || '').trim()) {
    n += 1;
  }
  return n;
};

/** Значение селекта: не задано — расчёт по тратам/триалу (как в ЛК). */
const PRIVILEGE_TIER_SELECT_AUTO = '__auto__';

const PRIVILEGE_TIER_SELECT_OPTIONS = [
  { value: PRIVILEGE_TIER_SELECT_AUTO, label: 'Авто (по тратам и правилам ЛК)' },
  { value: 'bronze', label: 'Бронза' },
  { value: 'silver', label: 'Серебро' },
  { value: 'gold', label: 'Золото' },
];

const PRIVILEGE_TIER_LABEL_RU = {
  bronze: 'Бронза',
  silver: 'Серебро',
  gold: 'Золото',
};

/** Как в ЛК (Settings / change-password): минимум 8 символов, буквы и цифры. */
const isCabinetPasswordPolicyOk = (raw) => {
  const p = String(raw ?? '');
  if (p.length < 8) {
    return false;
  }
  const hasLetter = /[a-zA-Zа-яА-ЯёЁ]/.test(p);
  const hasDigit = /[0-9]/.test(p);
  return hasLetter && hasDigit;
};

/** Как в личном кабинете: два знака после запятой (₽). */
const formatCabinetBalanceRub = (value) =>
  new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    Number(value) || 0,
  );

const getCabinetBalanceFromClient = (c) => {
  if (!c) {
    return 0;
  }
  const raw = c.balance_rub ?? c.cabinet_balance;
  if (raw === null || raw === undefined || raw === '') {
    return 0;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

const getDisplayTitle = (client) => {
  if (!client) {
    return '';
  }
  const raw = client.display_name || client.login || `Клиент ${client.id}`;
  return String(raw).toUpperCase();
};

const getLevelLabel = (client) => {
  const fromApi = client?.privilege_level && String(client.privilege_level).trim();
  if (fromApi) {
    return fromApi;
  }
  return 'Бронза';
};

const isBronzeLevel = (label) => {
  const t = String(label || '').toLowerCase();
  return t.includes('бронз');
};

const isGoldLevel = (label) => {
  const t = String(label || '').toLowerCase();
  return t.includes('золот');
};

const isSilverLevel = (label) => {
  const t = String(label || '').toLowerCase();
  return t.includes('сереб');
};

/** Иконка «Обновить» 12×12 (макет Figma, clipPath с уникальным id). */
const ToolbarSyncIcon = () => {
  const clipId = `cd-toolbar-sync-${useId().replace(/:/g, '')}`;
  return (
    <svg
      className="client-detail-drawer__toolbar-sync-icon"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <g clipPath={`url(#${clipId})`}>
        <path
          d="M6.0001 1.00014C6.66336 1.00231 7.31959 1.13601 7.93084 1.39348C8.54208 1.65096 9.09619 2.0271 9.5611 2.50014H8.0001C7.86749 2.50014 7.74032 2.55282 7.64655 2.64659C7.55278 2.74035 7.5001 2.86753 7.5001 3.00014C7.5001 3.13275 7.55278 3.25992 7.64655 3.35369C7.74032 3.44746 7.86749 3.50014 8.0001 3.50014H10.0716C10.3178 3.50001 10.5539 3.40214 10.728 3.22804C10.9021 3.05394 11 2.81785 11.0001 2.57164V0.500138C11.0001 0.36753 10.9474 0.240353 10.8537 0.146585C10.7599 0.0528164 10.6327 0.000137982 10.5001 0.000137982V0.000137982C10.3675 0.000137982 10.2403 0.0528164 10.1465 0.146585C10.0528 0.240353 10.0001 0.36753 10.0001 0.500138V1.53914C9.17369 0.794864 8.1564 0.295343 7.06215 0.0965125C5.96791 -0.102318 4.83991 0.00739396 3.80449 0.413362C2.76907 0.81933 1.86711 1.50552 1.19961 2.3951C0.532105 3.28468 0.125422 4.34251 0.0251013 5.45014C0.0186429 5.51977 0.0267591 5.58998 0.0489327 5.6563C0.0711063 5.72262 0.106851 5.78359 0.153889 5.83533C0.200928 5.88707 0.258228 5.92845 0.32214 5.95683C0.386052 5.9852 0.455174 5.99995 0.525101 6.00014V6.00014C0.647395 6.0017 0.765868 5.95756 0.857331 5.87636C0.948793 5.79516 1.00666 5.68276 1.0196 5.56114C1.13091 4.31651 1.70359 3.15849 2.62514 2.31456C3.54669 1.47062 4.75051 1.00178 6.0001 1.00014Z"
          fill="currentColor"
        />
        <path
          d="M11.4755 6.00004C11.3532 5.99848 11.2347 6.04262 11.1433 6.12382C11.0518 6.20501 10.9939 6.31742 10.981 6.43904C10.8983 7.39066 10.5445 8.2986 9.96133 9.05515C9.37819 9.8117 8.59028 10.3851 7.69107 10.7073C6.79187 11.0296 5.8191 11.0871 4.88818 10.8731C3.95725 10.6591 3.10725 10.1826 2.439 9.50005H4C4.13261 9.50005 4.25979 9.44737 4.35355 9.3536C4.44732 9.25983 4.5 9.13266 4.5 9.00005C4.5 8.86744 4.44732 8.74026 4.35355 8.64649C4.25979 8.55272 4.13261 8.50005 4 8.50005H1.9285C1.80655 8.49998 1.68578 8.52395 1.5731 8.57059C1.46042 8.61723 1.35804 8.68562 1.2718 8.77185C1.18557 8.85808 1.11718 8.96047 1.07054 9.07315C1.02391 9.18583 0.999934 9.3066 1 9.42855V11.5001C1 11.6327 1.05268 11.7598 1.14645 11.8536C1.24021 11.9474 1.36739 12.0001 1.5 12.0001C1.63261 12.0001 1.75979 11.9474 1.85355 11.8536C1.94732 11.7598 2 11.6327 2 11.5001V10.4611C2.82642 11.2053 3.84371 11.7048 4.93795 11.9037C6.0322 12.1025 7.16019 11.9928 8.19561 11.5868C9.23103 11.1809 10.133 10.4947 10.8005 9.60509C11.468 8.71551 11.8747 7.65767 11.975 6.55004C11.9815 6.48041 11.9733 6.4102 11.9512 6.34388C11.929 6.27756 11.8932 6.21659 11.8462 6.16485C11.7992 6.11311 11.7419 6.07173 11.678 6.04335C11.614 6.01498 11.5449 6.00023 11.475 6.00004H11.4755Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="12" height="12" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

/** Иконка выгрузки в тулбаре */
const ToolbarDownloadGlyph = () => (
  <span className="client-detail-drawer__toolbar-dl-glyph" aria-hidden>
    <span className="client-detail-drawer__toolbar-dl-glyph-v" />
    <span className="client-detail-drawer__toolbar-dl-glyph-h" />
  </span>
);

const LogThSortIcon = () => (
  <svg className="client-detail-drawer__log-th-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path d="M5 6L8 9L11 6" stroke="#7a7a7a" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Иконка воронки фильтра: активное состояние — акцент. */
const DrawerLogsFilterFunnelIcon = ({ filtered }) => {
  const clipId = `cd-drawer-log-funnel-${useId().replace(/:/g, '')}`;
  const color = filtered ? 'var(--pm-accent, #5283f4)' : '#7a7a7a';
  return (
    <svg
      className="client-detail-drawer__log-th-icon"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <g clipPath={`url(#${clipId})`}>
        <path
          d="M9.00005 14C8.89187 14 8.7866 13.9649 8.70005 13.9L6.70005 12.4C6.63795 12.3534 6.58755 12.293 6.55284 12.2236C6.51812 12.1542 6.50005 12.0776 6.50005 12V9.19L2.99205 5.2435C2.74291 4.96244 2.58024 4.61541 2.52359 4.24412C2.46694 3.87283 2.51872 3.49308 2.67271 3.15051C2.8267 2.80794 3.07635 2.51713 3.39164 2.31303C3.70694 2.10894 4.07446 2.00024 4.45005 2H11.5501C11.9256 2.00044 12.293 2.10932 12.6082 2.31355C12.9234 2.51779 13.1729 2.80869 13.3267 3.15129C13.4805 3.4939 13.5321 3.87364 13.4753 4.24487C13.4185 4.61611 13.2558 4.96305 13.0066 5.244L9.50005 9.19V13.5C9.50005 13.6326 9.44737 13.7598 9.3536 13.8536C9.25984 13.9473 9.13266 14 9.00005 14ZM7.50005 11.75L8.50005 12.5V9C8.50015 8.87758 8.54516 8.75945 8.62655 8.668L12.2606 4.5795C12.3817 4.44254 12.4608 4.27352 12.4883 4.09273C12.5157 3.91193 12.4905 3.72705 12.4154 3.56028C12.3404 3.39351 12.2189 3.25193 12.0654 3.15254C11.9118 3.05316 11.7329 3.00019 11.5501 3H4.45005C4.26728 3.00028 4.08846 3.05327 3.93505 3.15263C3.78164 3.25199 3.66015 3.3935 3.58516 3.56018C3.51016 3.72685 3.48483 3.91163 3.51221 4.09234C3.5396 4.27305 3.61854 4.44203 3.73955 4.579L7.37405 8.668C7.45526 8.75952 7.50009 8.87764 7.50005 9V11.75Z"
          fill={color}
          stroke={color}
          strokeWidth="0.6"
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="12" height="12" fill="white" transform="translate(2 2)" />
        </clipPath>
      </defs>
    </svg>
  );
};

/** Иконка сброса фильтров (как на странице «Клиенты»). */
const DrawerLogsClearFiltersIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      d="M3 4H15.5L9 13V19L7 17V13L3 7.58V4Z"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M17 8L22 13" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    <path d="M22 8L17 13" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
);

const LOG_FILTER_LEVEL_OPTS = [
  { value: '', label: 'Все' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'error', label: 'Error' },
];

const LOG_FILTER_CATEGORY_OPTS = [
  { value: '', label: 'Все' },
  { value: 'proxy', label: 'Прокси' },
  { value: 'autoreplace', label: 'Автозамена' },
];

const LOYALTY_CROWN_PATH_D =
  'M15.1773 3.04376C14.9337 2.94228 14.6654 2.91561 14.4066 2.96713C14.1478 3.01866 13.9102 3.14605 13.724 3.33309L12 5.05709L8.94267 1.99976C8.69263 1.7498 8.35355 1.60938 8 1.60938C7.64645 1.60938 7.30737 1.7498 7.05733 1.99976L4 5.05709L2.276 3.33309C2.08953 3.14668 1.85198 3.01974 1.59338 2.96831C1.33477 2.91689 1.06673 2.94329 0.823127 3.04419C0.579528 3.14508 0.37131 3.31593 0.224798 3.53515C0.0782854 3.75436 5.62991e-05 4.01209 0 4.27576L0 11.3331C0.00105857 12.2168 0.352588 13.0641 0.97748 13.6889C1.60237 14.3138 2.4496 14.6654 3.33333 14.6664H12.6667C13.5504 14.6654 14.3976 14.3138 15.0225 13.6889C15.6474 13.0641 15.9989 12.2168 16 11.3331V4.27576C16.0001 4.01207 15.9219 3.75428 15.7755 3.53499C15.6291 3.3157 15.4209 3.14475 15.1773 3.04376ZM14.6667 11.3331C14.6667 11.8635 14.456 12.3722 14.0809 12.7473C13.7058 13.1224 13.1971 13.3331 12.6667 13.3331H3.33333C2.8029 13.3331 2.29419 13.1224 1.91912 12.7473C1.54405 12.3722 1.33333 11.8635 1.33333 11.3331V4.27576L3.52867 6.47109C3.65369 6.59607 3.82322 6.66628 4 6.66628C4.17678 6.66628 4.34631 6.59607 4.47133 6.47109L8 2.94242L11.5287 6.47109C11.6537 6.59607 11.8232 6.66628 12 6.66628C12.1768 6.66628 12.3463 6.59607 12.4713 6.47109L14.6667 4.27576V11.3331Z';

const CrownIcon = ({ gradientIdPrefix = '', colorTint = '', className = '' }) => {
  if (!gradientIdPrefix || !colorTint) {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M8 3L9.5 6.5H13L10.25 8.5L11.5 12L8 10L4.5 12L5.75 8.5L3 6.5H6.5L8 3Z" fill="currentColor" />
      </svg>
    );
  }
  const metalId = `${gradientIdPrefix}-crown-metal`;
  const sheenId = `${gradientIdPrefix}-crown-sheen`;
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <defs>
        <linearGradient id={metalId} x1="0.887097" y1="9.77576" x2="15.4297" y2="5.60127" gradientUnits="userSpaceOnUse">
          <stop stopColor="#576265" />
          <stop offset="0.172016" stopColor="#9EA1A1" />
          <stop offset="0.45741" stopColor="#848B8A" />
          <stop offset="0.553567" stopColor="#576265" />
          <stop offset="0.823421" stopColor="#576265" />
          <stop offset="0.925155" stopColor="#757A7B" />
          <stop offset="1" stopColor="#576265" />
        </linearGradient>
        <linearGradient id={sheenId} x1="6.3871" y1="8.1379" x2="4.16685" y2="1.03771" gradientUnits="userSpaceOnUse">
          <stop stopColor="white" stopOpacity="0" />
          <stop offset="1" stopColor="white" />
        </linearGradient>
      </defs>
      <path d={LOYALTY_CROWN_PATH_D} fill={`url(#${metalId})`} />
      <path d={LOYALTY_CROWN_PATH_D} fill={`url(#${sheenId})`} style={{ mixBlendMode: 'overlay' }} />
      <path d={LOYALTY_CROWN_PATH_D} fill={colorTint} style={{ mixBlendMode: 'color' }} />
    </svg>
  );
};

const ChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronDoubleLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M11 17L6 12L11 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18 17L13 12L18 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronDoubleRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M13 7L18 12L13 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6 7L11 12L6 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowLink = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path d="M2 6H10M10 6L7 3M10 6L7 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const renderTariffBadge = (kind) => {
  if (kind === 'individual') {
    return (
      <span className="client-detail-drawer__badge client-detail-drawer__badge--blue">Индивидуальный</span>
    );
  }
  if (kind === 'common_plus') {
    return (
      <span className="client-detail-drawer__badge client-detail-drawer__badge--dark">Общий +</span>
    );
  }
  return <span className="client-detail-drawer__badge client-detail-drawer__badge--grey">Общий</span>;
};

/** Те же статусы/тон, что вкладка «Заказы» на дашборде ЛК. */
const renderCabinetPurchaseStatusBadge = (row) => {
  if (row.statusTone === 'pending') {
    return <span className="client-detail-drawer__badge client-detail-drawer__badge--pending">{row.statusLabel}</span>;
  }
  if (row.statusBadgeKind === 'refunded' || row.statusBadgeKind === 'cancelled') {
    return <span className="client-detail-drawer__badge client-detail-drawer__badge--red">{row.statusLabel}</span>;
  }
  if (row.statusBadgeKind === 'payment_failed') {
    return <span className="client-detail-drawer__badge client-detail-drawer__badge--orange">{row.statusLabel}</span>;
  }
  if (row.statusBadgeKind === 'renewal') {
    return <span className="client-detail-drawer__badge client-detail-drawer__badge--violet">{row.statusLabel}</span>;
  }
  if (row.statusBadgeKind === 'auto_renewal') {
    return <span className="client-detail-drawer__badge client-detail-drawer__badge--blue">{row.statusLabel}</span>;
  }
  return <span className="client-detail-drawer__badge client-detail-drawer__badge--ok">{row.statusLabel}</span>;
};

/** Бейдж статуса оплаты в карточке заказа (как в ЛК). */
const renderCabinetPaymentStatusBadgeDetail = (paymentStatus) => {
  const s = String(paymentStatus || '').toLowerCase();
  const label = paymentStatusLabelRu(paymentStatus);
  if (s === 'paid') {
    return <span className="client-detail-drawer__badge client-detail-drawer__badge--ok">{label}</span>;
  }
  if (s === 'failed') {
    return <span className="client-detail-drawer__badge client-detail-drawer__badge--red">{label}</span>;
  }
  if (s === 'expired') {
    return <span className="client-detail-drawer__badge client-detail-drawer__badge--grey">{label}</span>;
  }
  return <span className="client-detail-drawer__badge client-detail-drawer__badge--pending">{label}</span>;
};

const renderLogLevelBadge = (level) => {
  const l = String(level || 'info').toLowerCase();
  if (l === 'warning') {
    return (
      <span className="client-detail-drawer__log-pill client-detail-drawer__log-pill--level-warning">Warning</span>
    );
  }
  if (l === 'error') {
    return <span className="client-detail-drawer__log-pill client-detail-drawer__log-pill--level-error">Error</span>;
  }
  return <span className="client-detail-drawer__log-pill client-detail-drawer__log-pill--level-info">Info</span>;
};

const renderLogCategoryBadge = (category) => {
  const c = String(category || '').toLowerCase();
  if (c === 'autoreplace') {
    return (
      <span className="client-detail-drawer__log-pill client-detail-drawer__log-pill--cat-yellow">Автозамена</span>
    );
  }
  if (c === 'proxy') {
    return <span className="client-detail-drawer__log-pill client-detail-drawer__log-pill--cat-blue">Прокси</span>;
  }
  const label = category && String(category).trim() !== '' ? String(category) : '—';
  return (
    <span className="client-detail-drawer__log-pill client-detail-drawer__log-pill--cat-neutral">{label}</span>
  );
};

const LogColumnHeader = ({ label, filterLabel, filtered, filterContent, onPopoverOpenChange }) => (
  <div className="client-detail-drawer__th-inner client-detail-drawer__th--logs" role="presentation">
    <span className="client-detail-drawer__log-th-label">{label}</span>
    <span className="client-detail-drawer__log-th-tools">
      <LogThSortIcon />
      <Popover
        trigger={['click']}
        placement="bottomLeft"
        onOpenChange={onPopoverOpenChange}
        content={<div className="client-detail-drawer__log-filter-popover">{filterContent}</div>}
      >
        <button
          type="button"
          className={`client-detail-drawer__log-th-filter-btn${filtered ? ' client-detail-drawer__log-th-filter-btn--active' : ''}`}
          aria-label={`Фильтр: ${filterLabel}`}
          tabIndex={0}
        >
          <DrawerLogsFilterFunnelIcon filtered={filtered} />
        </button>
      </Popover>
    </span>
  </div>
);

const renderNumTone = (value, tone) => {
  const n = Number(value);
  if (tone === 'green') {
    return <span className="client-detail-drawer__cell-num client-detail-drawer__cell-num--green">{value}</span>;
  }
  if (tone === 'red') {
    return <span className="client-detail-drawer__cell-num client-detail-drawer__cell-num--red">{value}</span>;
  }
  if (tone === 'muted') {
    return <span className="client-detail-drawer__cell-num client-detail-drawer__cell-num--muted">{value}</span>;
  }
  const cls =
    n > 0
      ? 'client-detail-drawer__cell-num client-detail-drawer__cell-num--red'
      : 'client-detail-drawer__cell-num client-detail-drawer__cell-num--muted';
  return <span className={cls}>{value}</span>;
};

/** Поверх drawer клиента (как модалка карточки заказа). */
const CLIENT_DETAIL_REFUND_CONFIRM_MODAL_PROPS = {
  rootClassName: 'client-detail-drawer__refund-confirm-root',
  getContainer: () => document.body,
  zIndex: 10000,
  centered: true,
};

const ClientDetailDrawer = ({ client, open, onClose, onRefresh, onClientUpdated }) => {
  const [tab, setTab] = useState('info');
  const [historySubTab, setHistorySubTab] = useState('orders');
  /** На мобильной вкладка «Инфо» делится: сводка | заказы | операции */
  const [infoView, setInfoView] = useState('summary');
  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [showCabinetNewPassword, setShowCabinetNewPassword] = useState(false);
  const [showCabinetRepeatPassword, setShowCabinetRepeatPassword] = useState(false);
  const [cabinetPasswordSaving, setCabinetPasswordSaving] = useState(false);
  /** Plaintext proxy-auth пароль только после POST reveal; не пишем в client/list. */
  const [revealedProxyPassword, setRevealedProxyPassword] = useState(null);
  const [revealProxyPasswordLoading, setRevealProxyPasswordLoading] = useState(false);
  const [showRevealedProxyPassword, setShowRevealedProxyPassword] = useState(false);
  const [tgProxy, setTgProxy] = useState(true);
  const [tgServer, setTgServer] = useState(false);
  const [tgRenew, setTgRenew] = useState(false);
  const [cabinetProxyGroupRows, setCabinetProxyGroupRows] = useState([]);
  const [cabinetProxyGroupsLoading, setCabinetProxyGroupsLoading] = useState(false);
  const [cabinetBalanceDraft, setCabinetBalanceDraft] = useState('');
  const [personalDiscountDraft, setPersonalDiscountDraft] = useState('0');
  const [privilegeTierDraft, setPrivilegeTierDraft] = useState(PRIVILEGE_TIER_SELECT_AUTO);
  const [cabinetProfileSaving, setCabinetProfileSaving] = useState(false);
  const [skipForbiddenSaving, setSkipForbiddenSaving] = useState(false);
  const skipForbiddenSwitchId = useId();
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLogsTotal, setActivityLogsTotal] = useState(0);
  const [activityLogsOffset, setActivityLogsOffset] = useState(0);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [activityLogFilters, setActivityLogFilters] = useState(() => ({ ...INITIAL_CLIENT_ACTIVITY_LOG_FILTERS }));
  const [activityLogDraftTime, setActivityLogDraftTime] = useState(null);
  const [activityLogDraftAction, setActivityLogDraftAction] = useState('');
  const [activityLogDraftMessage, setActivityLogDraftMessage] = useState('');
  const [activityLogDraftUser, setActivityLogDraftUser] = useState('');
  const [activityLogDraftLevel, setActivityLogDraftLevel] = useState('');
  const [activityLogDraftCategory, setActivityLogDraftCategory] = useState('');
  const [cabinetBillingOrdersRaw, setCabinetBillingOrdersRaw] = useState([]);
  const [cabinetBillingOrdersLoading, setCabinetBillingOrdersLoading] = useState(false);
  const [cabinetBillingOrdersErr, setCabinetBillingOrdersErr] = useState(null);
  const [cabinetOrderDetailOpen, setCabinetOrderDetailOpen] = useState(false);
  const [cabinetOrderDetailLoading, setCabinetOrderDetailLoading] = useState(false);
  const [cabinetOrderDetailErr, setCabinetOrderDetailErr] = useState(null);
  const [cabinetOrderDetail, setCabinetOrderDetail] = useState(null);
  const [cabinetHistoryPage, setCabinetHistoryPage] = useState(1);
  const [refundingOrderId, setRefundingOrderId] = useState(null);
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  const lastBalanceClientIdRef = useRef(null);
  const lastDiscountClientIdRef = useRef(null);
  const lastPrivilegeClientIdRef = useRef(null);

  const balanceHeader = useMemo(() => {
    if (!client) {
      return formatCabinetBalanceRub(0);
    }
    return formatCabinetBalanceRub(getCabinetBalanceFromClient(client));
  }, [client]);

  const privilegeSpendChips = useMemo(() => {
    if (!client) {
      return { lifetime: '—', month: '—' };
    }
    const lt = client.cabinet_privilege_spend_lifetime_rub;
    const rolling30d = client.cabinet_privilege_spend_month_msk_rub;
    return {
      lifetime:
        typeof lt === 'number' && Number.isFinite(lt) ? formatCabinetBalanceRub(lt) : '—',
      month:
        typeof rolling30d === 'number' && Number.isFinite(rolling30d)
          ? formatCabinetBalanceRub(rolling30d)
          : '—',
    };
  }, [client]);

  const clientLoginForProxiesFilter = useMemo(() => {
    if (!client) {
      return '';
    }
    return String(client.cabinet_login || client.login || '').trim();
  }, [client]);

  const handleNavigateToProxiesWithClientFilter = useCallback(() => {
    if (!clientLoginForProxiesFilter) {
      message.warning('Не задан логин клиента для фильтра');
      return;
    }
    const params = new URLSearchParams();
    params.set('client_login', clientLoginForProxiesFilter);
    params.set('page', '1');
    openAppPathInNewTab(`/proxies?${params.toString()}`);
  }, [clientLoginForProxiesFilter]);

  // SM5/SM7: строки клиента в журналах Midori из S3 — «Журналы S3 → Поиск» с логином прокси клиента.
  const handleOpenJournalS3 = useCallback(() => {
    if (!client?.id) return;
    openAppPathInNewTab(`/journal-s3/search?client_id=${client.id}`);
  }, [client?.id]);

  useEffect(() => {
    if (!client) {
      setCabinetBalanceDraft('');
      lastBalanceClientIdRef.current = null;
      return;
    }
    const isClientChanged = lastBalanceClientIdRef.current !== client.id;
    if (!isClientChanged) {
      return;
    }
    // В админском профиле поле «Зачислить на баланс» — дельта изменения (можно +/-),
    // поэтому при открытии/смене клиента всегда начинаем с 0.
    setCabinetBalanceDraft('0');
    lastBalanceClientIdRef.current = client.id;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- только смена клиента
  }, [client?.id]);

  useEffect(() => {
    if (!client) {
      setPersonalDiscountDraft('0');
      lastDiscountClientIdRef.current = null;
      return;
    }
    const isClientChanged = lastDiscountClientIdRef.current !== client.id;
    const hasDiscountField = Object.prototype.hasOwnProperty.call(client, 'personal_discount_percent');
    if (!isClientChanged && !hasDiscountField) {
      return;
    }
    const rawDiscount = Number(client.personal_discount_percent);
    const normalizedDiscount = Number.isFinite(rawDiscount)
      ? Math.max(0, Math.min(100, rawDiscount))
      : 0;
    setPersonalDiscountDraft(String(Number(normalizedDiscount.toFixed(2))));
    lastDiscountClientIdRef.current = client.id;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- только id и скидка
  }, [client?.id, client?.personal_discount_percent]);

  useEffect(() => {
    if (!client) {
      setPrivilegeTierDraft(PRIVILEGE_TIER_SELECT_AUTO);
      lastPrivilegeClientIdRef.current = null;
      return;
    }
    const isClientChanged = lastPrivilegeClientIdRef.current !== client.id;
    const hasOverrideField = Object.prototype.hasOwnProperty.call(client, 'privilege_tier_override');
    if (!isClientChanged && !hasOverrideField) {
      return;
    }
    const o = client.privilege_tier_override;
    const low = o != null && String(o).trim() !== '' ? String(o).trim().toLowerCase() : '';
    if (low === 'bronze' || low === 'silver' || low === 'gold') {
      setPrivilegeTierDraft(low);
      lastPrivilegeClientIdRef.current = client.id;
      return;
    }
    setPrivilegeTierDraft(PRIVILEGE_TIER_SELECT_AUTO);
    lastPrivilegeClientIdRef.current = client.id;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- только id и override
  }, [client?.id, client?.privilege_tier_override]);

  /** Актуальный баланс с GET /api/clients/{id} — список может отдавать устаревшие поля. */
  useEffect(() => {
    if (!open || !client?.id || typeof onClientUpdated !== 'function') {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getClient(client.id);
        const data = res?.data;
        if (!data || cancelled) {
          return;
        }
        const bal = data.cabinet_balance;
        const patch = {
          ...data,
          balance_rub: bal,
          cabinet_balance: bal,
        };
        if (Object.prototype.hasOwnProperty.call(data, 'privilege_tier_override')) {
          patch.privilege_tier_override = data.privilege_tier_override;
        }
        if (Object.prototype.hasOwnProperty.call(data, 'privilege_level')) {
          patch.privilege_level = data.privilege_level;
        }
        if (Object.prototype.hasOwnProperty.call(data, 'personal_discount_percent')) {
          patch.personal_discount_percent = data.personal_discount_percent;
        }
        if (Object.prototype.hasOwnProperty.call(data, 'skip_forbidden_resources')) {
          patch.skip_forbidden_resources = data.skip_forbidden_resources;
        }
        if (Object.prototype.hasOwnProperty.call(data, 'cabinet_privilege_spend_lifetime_rub')) {
          patch.cabinet_privilege_spend_lifetime_rub = data.cabinet_privilege_spend_lifetime_rub;
        }
        if (Object.prototype.hasOwnProperty.call(data, 'cabinet_privilege_spend_month_msk_rub')) {
          patch.cabinet_privilege_spend_month_msk_rub = data.cabinet_privilege_spend_month_msk_rub;
        }
        onClientUpdated(patch);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, client?.id, onClientUpdated]);

  const groupTotals = useMemo(() => {
    const t = { proxy: 0, active: 0, inactive: 0, renew: 0 };
    cabinetProxyGroupRows.forEach((r) => {
      t.proxy += r.proxy;
      t.active += r.active;
      t.inactive += r.inactive;
      t.renew += r.renew;
    });
    return t;
  }, [cabinetProxyGroupRows]);

  const summaryMatrixRows = useMemo(() => {
    if (cabinetProxyGroupsLoading && cabinetProxyGroupRows.length === 0) {
      return [{ group: 'Загрузка…', proxy: null, active: null, inactive: null, renew: null, _loading: true }];
    }
    if (!cabinetProxyGroupsLoading && cabinetProxyGroupRows.length === 0) {
      return [{ group: 'Нет групп', proxy: 0, active: 0, inactive: 0, renew: 0, _empty: true }];
    }
    return cabinetProxyGroupRows;
  }, [cabinetProxyGroupRows, cabinetProxyGroupsLoading]);

  const showSummaryMatrixTotals = !cabinetProxyGroupsLoading || cabinetProxyGroupRows.length > 0;

  const handleGeneratePassword = useCallback(() => {
    const chars = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
    let s = '';
    for (let i = 0; i < 12; i += 1) {
      s += chars[Math.floor(Math.random() * chars.length)];
    }
    setNewPassword(s);
    setRepeatPassword(s);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia(`(max-width: ${CLIENT_DETAIL_MOBILE_MAX_PX}px)`);
    const handleChange = () => setIsMobileLayout(mq.matches);
    handleChange();
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (!open || !client?.id) {
      return undefined;
    }
    let cancelled = false;
    setCabinetProxyGroupsLoading(true);
    apiService
      .getClientCabinetProxyGroups(client.id)
      .then((response) => {
        if (cancelled) {
          return;
        }
        const raw = response?.data;
        const list = Array.isArray(raw) ? raw : [];
        const mapped = list.map((row) => ({
          group: row.group_name != null && String(row.group_name).trim() !== '' ? String(row.group_name) : '—',
          proxy: Number(row.proxy_count) || 0,
          active: Number(row.active_count) || 0,
          inactive: Number(row.inactive_count) || 0,
          renew: Number(row.renew_soon_count) || 0,
        }));
        setCabinetProxyGroupRows(mapped);
      })
      .catch(() => {
        if (!cancelled) {
          setCabinetProxyGroupRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCabinetProxyGroupsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, client?.id]);

  useLayoutEffect(() => {
    setActivityLogsOffset(0);
    setActivityLogFilters({ ...INITIAL_CLIENT_ACTIVITY_LOG_FILTERS });
    setActivityLogDraftTime(null);
    setActivityLogDraftAction('');
    setActivityLogDraftMessage('');
    setActivityLogDraftUser('');
    setActivityLogDraftLevel('');
    setActivityLogDraftCategory('');
  }, [client?.id]);

  useLayoutEffect(() => {
    setCabinetHistoryPage(1);
  }, [client?.id]);

  const fetchCabinetBillingOrders = useCallback(async () => {
    if (!client?.id) {
      return;
    }
    setCabinetBillingOrdersLoading(true);
    setCabinetBillingOrdersErr(null);
    try {
      const res = await apiService.getAdminClientCabinetOrders(client.id);
      const items = Array.isArray(res?.data?.items) ? res.data.items : [];
      setCabinetBillingOrdersRaw(items);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setCabinetBillingOrdersErr(typeof detail === 'string' ? detail : 'Не удалось загрузить заказы ЛК');
      setCabinetBillingOrdersRaw([]);
    } finally {
      setCabinetBillingOrdersLoading(false);
    }
  }, [client?.id]);

  useEffect(() => {
    if (!open || !client?.id) {
      return undefined;
    }
    fetchCabinetBillingOrders();
    return undefined;
  }, [open, client?.id, fetchCabinetBillingOrders]);

  const fetchActivityLogs = useCallback(
    async (offset) => {
      if (!client?.id) {
        return;
      }
      const o = Math.max(0, Number(offset) || 0);
      setActivityLogsLoading(true);
      try {
        const res = await apiService.getClientActivityLog(client.id, buildActivityLogRequestParams(o, activityLogFilters));
        const data = res?.data;
        const items = Array.isArray(data?.items) ? data.items : [];
        const total = Number(data?.total);
        setActivityLogs(items);
        setActivityLogsTotal(Number.isFinite(total) ? total : 0);
      } catch {
        message.error('Не удалось загрузить журнал');
        setActivityLogs([]);
        setActivityLogsTotal(0);
      } finally {
        setActivityLogsLoading(false);
      }
    },
    [client?.id, activityLogFilters],
  );

  const activeActivityLogFiltersCount = useMemo(
    () => countActiveActivityLogFilters(activityLogFilters),
    [activityLogFilters],
  );

  const handleClearAllActivityLogFilters = useCallback(() => {
    setActivityLogFilters({ ...INITIAL_CLIENT_ACTIVITY_LOG_FILTERS });
    setActivityLogDraftTime(null);
    setActivityLogDraftAction('');
    setActivityLogDraftMessage('');
    setActivityLogDraftUser('');
    setActivityLogDraftLevel('');
    setActivityLogDraftCategory('');
    setActivityLogsOffset(0);
  }, []);

  useEffect(() => {
    if (!open || !client?.id || tab !== 'logs') {
      return undefined;
    }
    fetchActivityLogs(activityLogsOffset);
    return undefined;
  }, [open, tab, client?.id, activityLogsOffset, fetchActivityLogs]);

  const clearRevealedProxyPassword = useCallback(() => {
    setRevealedProxyPassword(null);
    setShowRevealedProxyPassword(false);
    setRevealProxyPasswordLoading(false);
  }, []);

  useEffect(() => {
    clearRevealedProxyPassword();
  }, [client?.id, open, clearRevealedProxyPassword]);

  const handleRevealProxyPassword = useCallback(async () => {
    if (!client?.id || revealProxyPasswordLoading) {
      return;
    }
    setRevealProxyPasswordLoading(true);
    try {
      const res = await apiService.revealClientProxyPassword(client.id);
      const pwd = res?.data?.password;
      if (typeof pwd !== 'string') {
        message.error('Сервер не вернул пароль');
        return;
      }
      setRevealedProxyPassword(pwd);
      setShowRevealedProxyPassword(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        message.error('Недостаточно прав для просмотра пароля');
        return;
      }
      if (status === 404) {
        message.error('Клиент не найден');
        return;
      }
      const detail = err?.response?.data?.detail;
      const text =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d?.msg || d).join(' ')
            : err?.message || 'Не удалось получить пароль';
      message.error(text);
    } finally {
      setRevealProxyPasswordLoading(false);
    }
  }, [client?.id, revealProxyPasswordLoading]);

  const handleCopyRevealedProxyPassword = useCallback(async () => {
    const text = revealedProxyPassword != null ? String(revealedProxyPassword) : '';
    if (!text) {
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      message.success('Пароль скопирован');
    } catch {
      message.error('Не удалось скопировать');
    }
  }, [revealedProxyPassword]);

  const handleDrawerClose = useCallback(() => {
    setTab('info');
    setHistorySubTab('orders');
    setInfoView('summary');
    setNewPassword('');
    setRepeatPassword('');
    setShowCabinetNewPassword(false);
    setShowCabinetRepeatPassword(false);
    clearRevealedProxyPassword();
    setCabinetProxyGroupRows([]);
    setCabinetBillingOrdersRaw([]);
    setCabinetBillingOrdersErr(null);
    setCabinetOrderDetailOpen(false);
    setCabinetOrderDetailLoading(false);
    setCabinetOrderDetailErr(null);
    setCabinetOrderDetail(null);
    setCabinetHistoryPage(1);
    setRefundingOrderId(null);
    setActivityLogFilters({ ...INITIAL_CLIENT_ACTIVITY_LOG_FILTERS });
    setActivityLogDraftTime(null);
    setActivityLogDraftAction('');
    setActivityLogDraftMessage('');
    setActivityLogDraftUser('');
    setActivityLogDraftLevel('');
    setActivityLogDraftCategory('');
    if (typeof onClose === 'function') {
      onClose();
    }
  }, [onClose, clearRevealedProxyPassword]);

  const handleRefresh = useCallback(() => {
    if (typeof onRefresh === 'function') {
      onRefresh();
    }
    if (tab === 'logs' && client?.id) {
      fetchActivityLogs(activityLogsOffset);
    }
    if (open && client?.id) {
      fetchCabinetBillingOrders();
    }
  }, [onRefresh, tab, client?.id, fetchActivityLogs, activityLogsOffset, open, fetchCabinetBillingOrders]);

  const handleSaveCabinetPassword = useCallback(async () => {
    if (!client?.id) {
      return;
    }
    const p1 = String(newPassword);
    const p2 = String(repeatPassword);
    if (!p1) {
      message.error('Введите новый пароль');
      return;
    }
    if (!isCabinetPasswordPolicyOk(p1)) {
      message.error('Пароль: не короче 8 символов, нужны буквы и цифры.');
      return;
    }
    if (p1 !== p2) {
      message.error('Пароли не совпадают');
      return;
    }
    setCabinetPasswordSaving(true);
    try {
      const res = await apiService.updateClient(client.id, { password: p1 });
      const data = res?.data;
      if (data && typeof onClientUpdated === 'function') {
        const bal = data.cabinet_balance;
        onClientUpdated({
          ...data,
          balance_rub: bal,
          cabinet_balance: bal,
        });
      }
      message.success('Пароль для личного кабинета и прокси обновлён');
      setNewPassword('');
      setRepeatPassword('');
      setShowCabinetNewPassword(false);
      setShowCabinetRepeatPassword(false);
      clearRevealedProxyPassword();
      handleRefresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const text =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d?.msg || d).join(' ')
            : err?.message || 'Не удалось сохранить пароль';
      message.error(text);
    } finally {
      setCabinetPasswordSaving(false);
    }
  }, [client, newPassword, repeatPassword, handleRefresh, onClientUpdated, clearRevealedProxyPassword]);

  const syncActivityLogTimeDraft = useCallback(() => {
    setActivityLogDraftTime([
      activityLogFilters.dateFrom ? dayjs(activityLogFilters.dateFrom) : null,
      activityLogFilters.dateTo ? dayjs(activityLogFilters.dateTo) : null,
    ]);
  }, [activityLogFilters.dateFrom, activityLogFilters.dateTo]);

  const handleApplyActivityLogTimeFilter = useCallback(() => {
    let dateFrom = '';
    let dateTo = '';
    const r = activityLogDraftTime;
    const d0 = r && r[0];
    const d1 = r && r[1];
    if (d0 != null && typeof d0.isValid === 'function' && d0.isValid()) {
      dateFrom = d0.format('YYYY-MM-DD');
    }
    if (d1 != null && typeof d1.isValid === 'function' && d1.isValid()) {
      dateTo = d1.format('YYYY-MM-DD');
    } else if (dateFrom) {
      dateTo = dateFrom;
    }
    setActivityLogFilters((p) => ({ ...p, dateFrom, dateTo }));
    setActivityLogsOffset(0);
  }, [activityLogDraftTime]);

  const handleClearActivityLogTimeFilter = useCallback(() => {
    setActivityLogFilters((p) => ({ ...p, dateFrom: '', dateTo: '' }));
    setActivityLogDraftTime(null);
    setActivityLogsOffset(0);
  }, []);

  const handleApplyActivityLogLevelFilter = useCallback(() => {
    setActivityLogFilters((p) => ({ ...p, level: activityLogDraftLevel }));
    setActivityLogsOffset(0);
  }, [activityLogDraftLevel]);

  const handleClearActivityLogLevelFilter = useCallback(() => {
    setActivityLogDraftLevel('');
    setActivityLogFilters((p) => ({ ...p, level: '' }));
    setActivityLogsOffset(0);
  }, []);

  const handleApplyActivityLogCategoryFilter = useCallback(() => {
    setActivityLogFilters((p) => ({ ...p, category: activityLogDraftCategory }));
    setActivityLogsOffset(0);
  }, [activityLogDraftCategory]);

  const handleClearActivityLogCategoryFilter = useCallback(() => {
    setActivityLogDraftCategory('');
    setActivityLogFilters((p) => ({ ...p, category: '' }));
    setActivityLogsOffset(0);
  }, []);

  const handleApplyActivityLogActionFilter = useCallback(() => {
    setActivityLogFilters((p) => ({ ...p, action: activityLogDraftAction }));
    setActivityLogsOffset(0);
  }, [activityLogDraftAction]);

  const handleClearActivityLogActionFilter = useCallback(() => {
    setActivityLogDraftAction('');
    setActivityLogFilters((p) => ({ ...p, action: '' }));
    setActivityLogsOffset(0);
  }, []);

  const handleApplyActivityLogMessageFilter = useCallback(() => {
    setActivityLogFilters((p) => ({ ...p, message: activityLogDraftMessage }));
    setActivityLogsOffset(0);
  }, [activityLogDraftMessage]);

  const handleClearActivityLogMessageFilter = useCallback(() => {
    setActivityLogDraftMessage('');
    setActivityLogFilters((p) => ({ ...p, message: '' }));
    setActivityLogsOffset(0);
  }, []);

  const handleApplyActivityLogUserFilter = useCallback(() => {
    setActivityLogFilters((p) => ({ ...p, user: activityLogDraftUser }));
    setActivityLogsOffset(0);
  }, [activityLogDraftUser]);

  const handleClearActivityLogUserFilter = useCallback(() => {
    setActivityLogDraftUser('');
    setActivityLogFilters((p) => ({ ...p, user: '' }));
    setActivityLogsOffset(0);
  }, []);

  const handleActivityLogTimePopoverOpenChange = useCallback(
    (nextOpen) => {
      if (!nextOpen) {
        return;
      }
      syncActivityLogTimeDraft();
    },
    [syncActivityLogTimeDraft],
  );

  const handleActivityLogLevelPopoverOpenChange = useCallback(
    (nextOpen) => {
      if (!nextOpen) {
        return;
      }
      setActivityLogDraftLevel(String(activityLogFilters.level || '').trim().toLowerCase());
    },
    [activityLogFilters.level],
  );

  const handleActivityLogCategoryPopoverOpenChange = useCallback(
    (nextOpen) => {
      if (!nextOpen) {
        return;
      }
      setActivityLogDraftCategory(String(activityLogFilters.category || '').trim().toLowerCase());
    },
    [activityLogFilters.category],
  );

  const handleActivityLogActionPopoverOpenChange = useCallback(
    (nextOpen) => {
      if (!nextOpen) {
        return;
      }
      setActivityLogDraftAction(String(activityLogFilters.action ?? ''));
    },
    [activityLogFilters.action],
  );

  const handleActivityLogMessagePopoverOpenChange = useCallback(
    (nextOpen) => {
      if (!nextOpen) {
        return;
      }
      setActivityLogDraftMessage(String(activityLogFilters.message ?? ''));
    },
    [activityLogFilters.message],
  );

  const handleActivityLogUserPopoverOpenChange = useCallback(
    (nextOpen) => {
      if (!nextOpen) {
        return;
      }
      setActivityLogDraftUser(String(activityLogFilters.user ?? ''));
    },
    [activityLogFilters.user],
  );

  const handleSkipForbiddenResourcesChange = useCallback(async (checked) => {
    if (!client?.id) {
      return;
    }
    const previous = Boolean(client.skip_forbidden_resources);
    if (typeof onClientUpdated === 'function') {
      onClientUpdated({
        id: client.id,
        skip_forbidden_resources: checked,
      });
    }
    setSkipForbiddenSaving(true);
    try {
      const res = await apiService.updateClient(client.id, { skip_forbidden_resources: checked });
      const data = res?.data;
      if (data && typeof onClientUpdated === 'function') {
        onClientUpdated({
          id: data.id ?? client.id,
          skip_forbidden_resources: Boolean(data.skip_forbidden_resources),
        });
      }
      message.success(
        checked
          ? 'Запрещенные ресурсы не будут применяться при покупке прокси'
          : 'Запрещенные ресурсы снова применяются при покупке прокси',
      );
    } catch {
      if (typeof onClientUpdated === 'function') {
        onClientUpdated({
          id: client.id,
          skip_forbidden_resources: previous,
        });
      }
      message.error('Не удалось сохранить настройку «Не использовать запреты»');
    } finally {
      setSkipForbiddenSaving(false);
    }
  }, [client, onClientUpdated]);

  const handleSaveCabinetProfile = useCallback(async () => {
    if (!client?.id) {
      return;
    }
    const normalized = String(cabinetBalanceDraft).replace(',', '.').trim();
    const n = Number(normalized);
    if (normalized === '' || Number.isNaN(n)) {
      message.error('Введите корректную сумму изменения баланса');
      return;
    }
    const discountNormalized = String(personalDiscountDraft).replace(',', '.').trim();
    const discountValue = Number(discountNormalized);
    if (discountNormalized === '' || Number.isNaN(discountValue)) {
      message.error('Введите корректную персональную скидку');
      return;
    }
    if (discountValue < 0 || discountValue > 100) {
      message.error('Персональная скидка должна быть в диапазоне 0..100');
      return;
    }
    const balanceDelta = Math.round(n * 100) / 100;
    const currentBalance = getCabinetBalanceFromClient(client);
    const nextBalance = Math.round((currentBalance + balanceDelta) * 100) / 100;
    if (nextBalance < 0) {
      message.error('Итоговый баланс не может быть отрицательным');
      return;
    }
    const body = {
      // Для совместимости со старым backend отправляем итоговый абсолютный баланс.
      cabinet_balance: nextBalance,
      // Для нового backend отправляем явную дельту изменения.
      cabinet_balance_delta: balanceDelta,
      personal_discount_percent: Math.round(discountValue * 100) / 100,
      privilege_tier_override:
        privilegeTierDraft === PRIVILEGE_TIER_SELECT_AUTO ? '' : privilegeTierDraft,
    };
    setCabinetProfileSaving(true);
    try {
      const res = await apiService.updateClient(client.id, body);
      const data = res?.data;
      if (data && typeof onClientUpdated === 'function') {
        const hasOverrideField = Object.prototype.hasOwnProperty.call(data, 'privilege_tier_override');
        const savedOverride = hasOverrideField
          ? data.privilege_tier_override
          : privilegeTierDraft === PRIVILEGE_TIER_SELECT_AUTO
            ? null
            : privilegeTierDraft;
        const normalizedSavedOverride =
          savedOverride != null && String(savedOverride).trim() !== '' ? String(savedOverride).trim().toLowerCase() : null;
        const fallbackLevel =
          normalizedSavedOverride && PRIVILEGE_TIER_LABEL_RU[normalizedSavedOverride]
            ? PRIVILEGE_TIER_LABEL_RU[normalizedSavedOverride]
            : data.privilege_level;
        const bal = data.cabinet_balance;
        onClientUpdated({
          ...data,
          balance_rub: bal,
          cabinet_balance: bal,
          personal_discount_percent:
            Object.prototype.hasOwnProperty.call(data, 'personal_discount_percent')
              ? data.personal_discount_percent
              : body.personal_discount_percent,
          privilege_tier_override: savedOverride,
          privilege_level: fallbackLevel,
        });
      }
      message.success('Сохранено');
      setCabinetBalanceDraft('0');
      handleRefresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const text =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d?.msg || d).join(' ')
            : err?.message || 'Не удалось сохранить';
      message.error(text);
    } finally {
      setCabinetProfileSaving(false);
    }
  }, [client, cabinetBalanceDraft, personalDiscountDraft, privilegeTierDraft, handleRefresh, onClientUpdated]);

  const handleExportLogs = useCallback(() => {
    if (!client?.id || activityLogs.length === 0) {
      message.info('Нет записей для выгрузки');
      return;
    }
    try {
      const blob = new Blob([JSON.stringify(activityLogs, null, 2)], {
        type: 'application/json;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `client-${client.id}-journal.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Не удалось выгрузить журнал');
    }
  }, [client?.id, activityLogs]);

  const handleExportProfile = useCallback(() => {
    // Заглушка: при появлении API — выгрузка профиля
  }, []);

  const handleSaveProfile = useCallback(() => {
    // Заглушка: при появлении API — сохранение данных пользователя
  }, []);

  const profileUserFields = useMemo(() => {
    const formatDash = (value) => {
      if (value == null) {
        return '—';
      }
      const s = String(value).trim();
      return s === '' ? '—' : s;
    };
    const tgRaw = client?.telegram_username;
    const tgDisplay =
      tgRaw != null && String(tgRaw).trim() !== ''
        ? String(tgRaw).trim().startsWith('@')
          ? String(tgRaw).trim()
          : `@${String(tgRaw).trim()}`
        : '—';
    const loginDisplay = formatDash(client?.cabinet_login || client?.login);
    const emailRaw = client?.cabinet_email ?? client?.email ?? '';
    const emailDisplay = normalizeClientCabinetEmailDisplay(emailRaw) || '—';
    return [
      { label: 'ID клиента', value: client?.id != null ? String(client.id) : '—', id: 'cd-client-id', accent: true },
      { label: 'Логин', value: loginDisplay, id: 'cd-login', accent: false },
      { label: 'Фамилия', value: formatDash(client?.last_name), id: 'cd-last', accent: false },
      { label: 'Имя', value: formatDash(client?.first_name), id: 'cd-first', accent: false },
      { label: 'Отчество', value: formatDash(client?.middle_name), id: 'cd-mid', accent: false },
      { label: 'Телеграмм', value: tgDisplay, id: 'cd-tg', accent: false },
      { label: 'Номер телефона', value: formatDash(client?.phone), id: 'cd-phone', accent: true },
      { label: 'Электронная почта', value: emailDisplay, id: 'cd-email', accent: false },
    ];
  }, [client]);

  const renderProxyPasswordRevealField = (opts = {}) => {
    const { idSuffix = '', fieldClassName = '', inputClassName = '', toggleClassName = '' } = opts;
    const inputId = `cd-proxy-password${idSuffix}`;
    const hasRevealed = revealedProxyPassword != null;

    return (
      <div className={`client-detail-drawer__field${fieldClassName ? ` ${fieldClassName}` : ''}`}>
        <label className="client-detail-drawer__field-label" htmlFor={inputId}>
          Пароль прокси
        </label>
        {!hasRevealed ? (
          <button
            type="button"
            className="client-detail-drawer__btn-outline client-detail-drawer__btn-outline--reveal-password"
            onClick={handleRevealProxyPassword}
            disabled={revealProxyPasswordLoading || !client?.id}
            aria-label="Показать пароль прокси клиента"
            tabIndex={0}
          >
            {revealProxyPasswordLoading ? 'Загрузка…' : 'Показать пароль'}
          </button>
        ) : (
          <>
            <div className="client-detail-drawer__password-input-wrap client-detail-drawer__password-input-wrap--reveal">
              <input
                id={inputId}
                type={showRevealedProxyPassword ? 'text' : 'password'}
                readOnly
                autoComplete="off"
                className={`client-detail-drawer__field-input client-detail-drawer__field-input--with-toggle${inputClassName ? ` ${inputClassName}` : ''}`}
                value={revealedProxyPassword}
                aria-label="Пароль прокси клиента"
                tabIndex={0}
              />
              <button
                type="button"
                className={`client-detail-drawer__password-toggle${toggleClassName ? ` ${toggleClassName}` : ''}`}
                onClick={() => setShowRevealedProxyPassword((v) => !v)}
                aria-label={showRevealedProxyPassword ? 'Скрыть пароль' : 'Показать пароль'}
                aria-pressed={showRevealedProxyPassword}
                tabIndex={0}
              >
                {showRevealedProxyPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </button>
            </div>
            <div className="client-detail-drawer__reveal-password-actions" role="group" aria-label="Действия с паролем прокси">
              <button
                type="button"
                className="client-detail-drawer__btn-outline client-detail-drawer__btn-outline--reveal-password"
                onClick={handleCopyRevealedProxyPassword}
                aria-label="Скопировать пароль прокси"
                tabIndex={0}
              >
                <CopyOutlined aria-hidden />
                <span>Копировать</span>
              </button>
              <button
                type="button"
                className="client-detail-drawer__btn-outline client-detail-drawer__btn-outline--reveal-password"
                onClick={clearRevealedProxyPassword}
                aria-label="Скрыть пароль прокси"
                tabIndex={0}
              >
                Скрыть
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  const logsPageCount = useMemo(() => Math.max(1, Math.ceil(activityLogsTotal / LOGS_PAGE_SIZE) || 1), [
    activityLogsTotal,
  ]);
  const logsCurrentPage = Math.min(
    logsPageCount,
    Math.floor(activityLogsOffset / LOGS_PAGE_SIZE) + 1,
  );
  const logTotal = activityLogsTotal;
  const logRangeStart =
    activityLogsTotal === 0 || activityLogs.length === 0 ? 0 : activityLogsOffset + 1;
  const logRangeEnd = activityLogsOffset + activityLogs.length;

  const handleLogsFirst = useCallback(() => {
    setActivityLogsOffset(0);
  }, []);
  const handleLogsPrev = useCallback(() => {
    setActivityLogsOffset((o) => Math.max(0, o - LOGS_PAGE_SIZE));
  }, []);
  const handleLogsNext = useCallback(() => {
    setActivityLogsOffset((o) => {
      const next = o + LOGS_PAGE_SIZE;
      return next < activityLogsTotal ? next : o;
    });
  }, [activityLogsTotal]);
  const handleLogsLast = useCallback(() => {
    setActivityLogsOffset(
      Math.max(0, (Math.ceil(activityLogsTotal / LOGS_PAGE_SIZE) - 1) * LOGS_PAGE_SIZE),
    );
  }, [activityLogsTotal]);

  const logsPrevDisabled = activityLogsOffset <= 0 || activityLogsLoading || activityLogsTotal === 0;
  const logsNextDisabled =
    activityLogsLoading ||
    activityLogsTotal === 0 ||
    activityLogsOffset + LOGS_PAGE_SIZE >= activityLogsTotal;

  /** Плашка в шапке: выбор в селекте (до сохранения) или сохранённый уровень при «Авто». */
  const levelLabel = useMemo(() => {
    if (!client) {
      return 'Бронза';
    }
    if (privilegeTierDraft === PRIVILEGE_TIER_SELECT_AUTO) {
      return getLevelLabel(client);
    }
    const ru = { bronze: 'Бронза', silver: 'Серебро', gold: 'Золото' };
    return ru[privilegeTierDraft] || getLevelLabel(client);
  }, [client, privilegeTierDraft]);

  const showBronzeWordmark = isBronzeLevel(levelLabel);
  const showGoldWordmark = isGoldLevel(levelLabel);
  const showSilverWordmark = isSilverLevel(levelLabel);

  const drawerWidth = useMemo(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= CLIENT_DETAIL_MOBILE_MAX_PX) {
      return '100%';
    }
    if (isMobileLayout) {
      return '100%';
    }
    return CLIENT_DETAIL_DRAWER_DESKTOP_WIDTH_CSS;
  }, [isMobileLayout]);

  const handleTabInfo = useCallback(() => {
    setTab('info');
    setInfoView('summary');
  }, []);

  const handleMobileSummary = useCallback(() => {
    setTab('info');
    setInfoView('summary');
  }, []);

  const handleMobileOrders = useCallback(() => {
    setTab('info');
    setInfoView('orders');
    setHistorySubTab('orders');
    setCabinetHistoryPage(1);
  }, []);

  const handleMobileOperations = useCallback(() => {
    setTab('info');
    setInfoView('operations');
    setHistorySubTab('operations');
    setCabinetHistoryPage(1);
  }, []);

  const purchaseHistoryRows = useMemo(
    () => purchaseHistoryRowsFromBillingItems(cabinetBillingOrdersRaw),
    [cabinetBillingOrdersRaw],
  );
  const operationHistoryRows = useMemo(
    () => operationHistoryRowsFromBillingItems(cabinetBillingOrdersRaw),
    [cabinetBillingOrdersRaw],
  );
  const ordersHistoryCount = purchaseHistoryRows.length;
  const operationsHistoryCount = operationHistoryRows.length;

  const isOrdersHistorySlice = historySubTab === 'orders';
  const totalCabinetHistory = isOrdersHistorySlice ? ordersHistoryCount : operationsHistoryCount;
  const cabinetHistoryTotalPages = Math.max(1, Math.ceil(totalCabinetHistory / CABINET_HISTORY_PAGE_SIZE));
  const cabinetHistoryCurrentPage = Math.min(cabinetHistoryPage, cabinetHistoryTotalPages);
  const cabinetHistoryStart = (cabinetHistoryCurrentPage - 1) * CABINET_HISTORY_PAGE_SIZE;
  const visiblePurchaseHistoryRows = purchaseHistoryRows.slice(
    cabinetHistoryStart,
    cabinetHistoryStart + CABINET_HISTORY_PAGE_SIZE,
  );
  const visibleOperationHistoryRows = operationHistoryRows.slice(
    cabinetHistoryStart,
    cabinetHistoryStart + CABINET_HISTORY_PAGE_SIZE,
  );
  const cabinetHistoryRangeStart = totalCabinetHistory > 0 ? cabinetHistoryStart + 1 : 0;
  const cabinetHistoryRangeEnd = Math.min(cabinetHistoryStart + CABINET_HISTORY_PAGE_SIZE, totalCabinetHistory);

  const handleHistorySubTabOrders = useCallback(() => {
    setHistorySubTab('orders');
    setCabinetHistoryPage(1);
  }, []);
  const handleHistorySubTabOperations = useCallback(() => {
    setHistorySubTab('operations');
    setCabinetHistoryPage(1);
  }, []);

  const handleCabinetHistoryFirst = useCallback(() => setCabinetHistoryPage(1), []);
  const handleCabinetHistoryPrev = useCallback(() => {
    setCabinetHistoryPage((p) => Math.max(1, p - 1));
  }, []);
  const handleCabinetHistoryNext = useCallback(() => {
    setCabinetHistoryPage((p) => Math.min(cabinetHistoryTotalPages, p + 1));
  }, [cabinetHistoryTotalPages]);
  const handleCabinetHistoryLast = useCallback(() => {
    setCabinetHistoryPage(cabinetHistoryTotalPages);
  }, [cabinetHistoryTotalPages]);

  const handleOperationRefundClick = useCallback(
    (row) => {
      if (!client?.id || !row?.orderId || !row.canRefund) {
        return;
      }
      const viaBalance = row.refundProvider === 'cabinet_balance';
      const isCabinetTopup = Boolean(row.isCabinetTopup);
      const isRefunding = refundingOrderId === row.orderId;
      Modal.confirm({
        ...CLIENT_DETAIL_REFUND_CONFIRM_MODAL_PROPS,
        title: 'Подтвердите возврат',
        content: (
          <div className="client-detail-drawer__refund-confirm">
            <p className="client-detail-drawer__refund-confirm-lead">
              {viaBalance
                ? 'Сумма будет возвращена на баланс личного кабинета клиента (оплата была с баланса).'
                : isCabinetTopup
                  ? 'Средства вернутся на карту через АТОЛ Pay, сумма пополнения будет списана с баланса ЛК.'
                  : 'Средства будут возвращены клиенту на карту через АТОЛ Pay.'}
            </p>
            <p className="client-detail-drawer__refund-confirm-amount">
              <span className="client-detail-drawer__refund-confirm-label">Сумма возврата: </span>
              <strong>{row.refundAmountFormatted}</strong>
            </p>
            <p className="client-detail-drawer__refund-confirm-meta">{row.typeLabel}</p>
          </div>
        ),
        okText: 'Возврат',
        cancelText: 'Отмена',
        okButtonProps: { danger: true },
        okType: 'danger',
        onOk: async () => {
          if (isRefunding) {
            return;
          }
          setRefundingOrderId(row.orderId);
          try {
            const res = await apiService.postAdminClientCabinetOrderRefund(client.id, row.orderId);
            const msg = res?.data?.message;
            message.success(
              typeof msg === 'string'
                ? msg
                : viaBalance
                  ? `Возврат ${row.refundAmountFormatted} зачислен на баланс`
                  : `Возврат ${row.refundAmountFormatted} инициирован в АТОЛ`,
            );
            if (typeof onClientUpdated === 'function') {
              const balanceAfter = res?.data?.balance_after;
              if (typeof balanceAfter === 'number' && Number.isFinite(balanceAfter)) {
                onClientUpdated({
                  ...client,
                  balance_rub: balanceAfter,
                  cabinet_balance: balanceAfter,
                });
              } else if (viaBalance && res?.data?.refund_method === 'cabinet_balance') {
                const currentBal = getCabinetBalanceFromClient(client);
                onClientUpdated({
                  ...client,
                  balance_rub: Math.round((currentBal + (row.refundAmountRub || 0)) * 100) / 100,
                  cabinet_balance: Math.round((currentBal + (row.refundAmountRub || 0)) * 100) / 100,
                });
              }
            }
            await fetchCabinetBillingOrders();
            handleRefresh();
          } catch (e) {
            const detail = e?.response?.data?.detail;
            message.error(typeof detail === 'string' ? detail : 'Не удалось выполнить возврат');
            throw e;
          } finally {
            setRefundingOrderId(null);
          }
        },
      });
    },
    [client, fetchCabinetBillingOrders, handleRefresh, onClientUpdated, refundingOrderId],
  );

  const handlePurchaseOrderCancelClick = useCallback(
    (row) => {
      if (!client?.id || !row?.orderId || !row.canCancelStuck) {
        return;
      }
      const isCancelling = cancellingOrderId === row.orderId;
      Modal.confirm({
        ...CLIENT_DETAIL_REFUND_CONFIRM_MODAL_PROPS,
        title: 'Отменить зависший заказ?',
        content: (
          <div className="client-detail-drawer__refund-confirm">
            <p className="client-detail-drawer__refund-confirm-lead">
              Заказ или продление будет аннулировано, задачи выдачи в очереди остановятся. Сумма оплаты вернётся
              на баланс личного кабинета клиента.
            </p>
            <p className="client-detail-drawer__refund-confirm-amount">
              <span className="client-detail-drawer__refund-confirm-label">Сумма возврата: </span>
              <strong>{row.cancelAmountFormatted}</strong>
            </p>
            <p className="client-detail-drawer__refund-confirm-meta">{row.orderNumber}</p>
          </div>
        ),
        okText: 'Отменить заказ',
        cancelText: 'Назад',
        okButtonProps: { danger: true },
        okType: 'danger',
        onOk: async () => {
          if (isCancelling) {
            return;
          }
          setCancellingOrderId(row.orderId);
          try {
            const res = await apiService.postAdminClientCabinetOrderCancelStuck(client.id, row.orderId);
            const msg = res?.data?.message;
            message.success(typeof msg === 'string' ? msg : `Заказ отменён, ${row.cancelAmountFormatted} на баланс`);
            if (typeof onClientUpdated === 'function') {
              const balanceAfter = res?.data?.balance_after;
              if (typeof balanceAfter === 'number' && Number.isFinite(balanceAfter)) {
                onClientUpdated({
                  ...client,
                  balance_rub: balanceAfter,
                  cabinet_balance: balanceAfter,
                });
              } else {
                const currentBal = getCabinetBalanceFromClient(client);
                onClientUpdated({
                  ...client,
                  balance_rub: Math.round((currentBal + (row.cancelAmountRub || 0)) * 100) / 100,
                  cabinet_balance: Math.round((currentBal + (row.cancelAmountRub || 0)) * 100) / 100,
                });
              }
            }
            await fetchCabinetBillingOrders();
            handleRefresh();
          } catch (e) {
            const detail = e?.response?.data?.detail;
            message.error(typeof detail === 'string' ? detail : 'Не удалось отменить заказ');
            throw e;
          } finally {
            setCancellingOrderId(null);
          }
        },
      });
    },
    [cancellingOrderId, client, fetchCabinetBillingOrders, handleRefresh, onClientUpdated],
  );

  const handleCabinetOrderRowActivate = useCallback(
    async (orderId) => {
      if (!client?.id || !Number.isFinite(Number(orderId))) {
        return;
      }
      setCabinetOrderDetailOpen(true);
      setCabinetOrderDetailLoading(true);
      setCabinetOrderDetailErr(null);
      setCabinetOrderDetail(null);
      try {
        const res = await apiService.getAdminClientCabinetOrder(client.id, orderId);
        setCabinetOrderDetail(res?.data ?? null);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        setCabinetOrderDetailErr(typeof detail === 'string' ? detail : 'Не удалось загрузить заказ');
      } finally {
        setCabinetOrderDetailLoading(false);
      }
    },
    [client?.id],
  );

  const handleCabinetOrderDetailModalClose = useCallback(() => {
    setCabinetOrderDetailOpen(false);
    setCabinetOrderDetailErr(null);
    setCabinetOrderDetail(null);
  }, []);

  // Затемнение окна заказа закрывает только настоящим щелчком (владелец 29.10, DF85)
  const cabinetOrderDetailBackdrop = useBackdropClose(handleCabinetOrderDetailModalClose);

  const handleOrderNumKeyDown = useCallback(
    (event, orderId) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
      void handleCabinetOrderRowActivate(orderId);
    },
    [handleCabinetOrderRowActivate],
  );

  const cabinetHistoryFirstDisabled =
    cabinetHistoryCurrentPage <= 1 || cabinetBillingOrdersLoading || totalCabinetHistory === 0;
  const cabinetHistoryPrevDisabled =
    cabinetHistoryCurrentPage <= 1 || cabinetBillingOrdersLoading || totalCabinetHistory === 0;
  const cabinetHistoryNextDisabled =
    cabinetHistoryCurrentPage >= cabinetHistoryTotalPages || cabinetBillingOrdersLoading || totalCabinetHistory === 0;
  const cabinetHistoryLastDisabled =
    cabinetHistoryCurrentPage >= cabinetHistoryTotalPages || cabinetBillingOrdersLoading || totalCabinetHistory === 0;

  const renderOrdersTableBlock = () => (
    <Spin spinning={cabinetBillingOrdersLoading}>
      <>
        {cabinetBillingOrdersErr ? (
          <div className="client-detail-drawer__history-error" role="alert">
            {cabinetBillingOrdersErr}
          </div>
        ) : null}
        {!cabinetBillingOrdersLoading &&
        !cabinetBillingOrdersErr &&
        visiblePurchaseHistoryRows.length === 0 &&
        purchaseHistoryRows.length === 0 ? (
          <div className="client-detail-drawer__history-empty">Заказов пока нет</div>
        ) : null}
        <div className="client-detail-drawer__history-table-wrap">
          <table
            className="client-detail-drawer__table client-detail-drawer__table--orders"
            aria-label="Заказы клиента"
          >
            <colgroup>
              <col className="client-detail-drawer__table-col--orders-num" />
              <col className="client-detail-drawer__table-col--orders-fixed" />
              <col className="client-detail-drawer__table-col--orders-fixed" />
              <col className="client-detail-drawer__table-col--orders-status" />
              <col className="client-detail-drawer__table-col--orders-fixed-sm" />
              <col className="client-detail-drawer__table-col--orders-fixed-sm" />
              <col className="client-detail-drawer__table-col--orders-fixed-sm" />
              <col className="client-detail-drawer__table-col--orders-fixed-sm" />
              <col className="client-detail-drawer__table-col--orders-tariff" />
              <col className="client-detail-drawer__table-col--orders-actions" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className="client-detail-drawer__th client-detail-drawer__th--orders-num">
                  Номер
                </th>
                <th scope="col" className="client-detail-drawer__th">
                  Дата
                </th>
                <th scope="col" className="client-detail-drawer__th">
                  Сумма
                </th>
                <th scope="col" className="client-detail-drawer__th">
                  Статус
                </th>
                <th scope="col" className="client-detail-drawer__th">
                  Прокси
                </th>
                <th scope="col" className="client-detail-drawer__th">
                  Активно
                </th>
                <th scope="col" className="client-detail-drawer__th">
                  Неактивно
                </th>
                <th scope="col" className="client-detail-drawer__th">
                  Тариф
                </th>
                <th scope="col" className="client-detail-drawer__th client-detail-drawer__th--orders-actions">
                  <span className="client-detail-drawer__sr-only">Действия</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visiblePurchaseHistoryRows.map((r, idx) => {
                const n = Number(r.active);
                return (
                  <tr key={`ord-row-${r.orderId}-${idx}`}>
                    <td className="client-detail-drawer__td client-detail-drawer__td--neutral client-detail-drawer__td--order-num">
                      <button
                        type="button"
                        className="client-detail-drawer__order-num-btn"
                        tabIndex={0}
                        title="Открыть карточку заказа"
                        aria-label={`Заказ ${r.orderNumber}, открыть карточку`}
                        onClick={() => void handleCabinetOrderRowActivate(r.orderId)}
                        onKeyDown={(e) => handleOrderNumKeyDown(e, r.orderId)}
                      >
                        {r.orderNumber}
                      </button>
                    </td>
                    <td className="client-detail-drawer__td">
                      <span className="client-detail-drawer__cell-date">{r.date}</span>
                    </td>
                    <td className="client-detail-drawer__td client-detail-drawer__td--right">
                      <span className="client-detail-drawer__td--neutral">{r.amountFormatted}</span>
                    </td>
                    <td className="client-detail-drawer__td client-detail-drawer__td--center">
                      {renderCabinetPurchaseStatusBadge(r)}
                    </td>
                    <td className="client-detail-drawer__td">
                      <span className="client-detail-drawer__cell-num client-detail-drawer__cell-num--proxy">{r.proxies}</span>
                    </td>
                    <td className="client-detail-drawer__td">{renderNumTone(r.active, n > 0 ? 'green' : 'muted')}</td>
                    <td className="client-detail-drawer__td">{renderNumTone(r.inactive)}</td>
                    <td className="client-detail-drawer__td client-detail-drawer__td--center client-detail-drawer__td--orders-tariff">
                      {renderTariffBadge(tariffTariffKindFromCabinetName(r.tariffName))}
                    </td>
                    <td className="client-detail-drawer__td client-detail-drawer__td--center client-detail-drawer__td--orders-actions">
                      {r.canCancelStuck ? (
                        <button
                          type="button"
                          className="client-detail-drawer__op-refund-btn client-detail-drawer__order-cancel-btn"
                          aria-label={`Отменить зависший заказ ${r.orderNumber}, вернуть ${r.cancelAmountFormatted} на баланс`}
                          disabled={cabinetBillingOrdersLoading || cancellingOrderId === r.orderId}
                          onClick={() => handlePurchaseOrderCancelClick(r)}
                        >
                          {cancellingOrderId === r.orderId ? '…' : 'Отмена'}
                        </button>
                      ) : r.isCancelled ? (
                        <span
                          className="client-detail-drawer__op-badge client-detail-drawer__op-badge--grey client-detail-drawer__op-refund-done"
                          role="status"
                          aria-label="Заказ отменён"
                        >
                          Отменён
                        </span>
                      ) : (
                        <span className="client-detail-drawer__op-actions-empty" aria-hidden>
                          —
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="client-detail-drawer__orders-foot">
          <span className="client-detail-drawer__orders-foot-range">
            {ordersHistoryCount === 0
              ? '0'
              : `${cabinetHistoryRangeStart}-${cabinetHistoryRangeEnd}`}{' '}
            из {ordersHistoryCount} записей
          </span>
          <div className="client-detail-drawer__pagination" role="navigation" aria-label="Страницы заказов">
            <button
              type="button"
              className={`client-detail-drawer__page-icon${cabinetHistoryFirstDisabled ? ' client-detail-drawer__page-icon--muted' : ''}`}
              aria-label="Первая страница"
              disabled={cabinetHistoryFirstDisabled}
              onClick={handleCabinetHistoryFirst}
            >
              <ChevronDoubleLeft />
            </button>
            <button
              type="button"
              className={`client-detail-drawer__page-icon${cabinetHistoryPrevDisabled ? ' client-detail-drawer__page-icon--muted' : ''}`}
              aria-label="Предыдущая страница"
              disabled={cabinetHistoryPrevDisabled}
              onClick={handleCabinetHistoryPrev}
            >
              <ChevronLeft />
            </button>
            {Array.from({ length: cabinetHistoryTotalPages }, (_, i) => {
              const page = i + 1;
              const isActive = page === cabinetHistoryCurrentPage;
              return (
                <button
                  key={page}
                  type="button"
                  className={isActive ? 'client-detail-drawer__page-current' : 'client-detail-drawer__page-num'}
                  aria-label={`Страница ${page}`}
                  aria-current={isActive ? 'page' : undefined}
                  disabled={isActive || cabinetBillingOrdersLoading || ordersHistoryCount === 0}
                  onClick={() => setCabinetHistoryPage(page)}
                >
                  {page}
                </button>
              );
            })}
            <button
              type="button"
              className={`client-detail-drawer__page-icon${cabinetHistoryNextDisabled ? ' client-detail-drawer__page-icon--muted' : ''}`}
              aria-label="Следующая страница"
              disabled={cabinetHistoryNextDisabled}
              onClick={handleCabinetHistoryNext}
            >
              <ChevronRight />
            </button>
            <button
              type="button"
              className={`client-detail-drawer__page-icon${cabinetHistoryLastDisabled ? ' client-detail-drawer__page-icon--muted' : ''}`}
              aria-label="Последняя страница"
              disabled={cabinetHistoryLastDisabled}
              onClick={handleCabinetHistoryLast}
            >
              <ChevronDoubleRight />
            </button>
          </div>
        </div>
      </>
    </Spin>
  );

  const renderOperationsTableBlock = () => (
    <Spin spinning={cabinetBillingOrdersLoading}>
      <>
        {cabinetBillingOrdersErr ? (
          <div className="client-detail-drawer__history-error" role="alert">
            {cabinetBillingOrdersErr}
          </div>
        ) : null}
        {!cabinetBillingOrdersLoading &&
        !cabinetBillingOrdersErr &&
        visibleOperationHistoryRows.length === 0 &&
        operationHistoryRows.length === 0 ? (
          <div className="client-detail-drawer__history-empty">Операции по счёту появятся позже</div>
        ) : null}
        <div className="client-detail-drawer__history-table-wrap">
          <table
            className="client-detail-drawer__table client-detail-drawer__table--operations"
            aria-label="Операции по счёту"
          >
            <colgroup>
              <col className="client-detail-drawer__table-col--op-date" />
              <col className="client-detail-drawer__table-col--op-type" />
              <col className="client-detail-drawer__table-col--op-sum" />
              <col className="client-detail-drawer__table-col--op-actions" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className="client-detail-drawer__th">
                  Дата
                </th>
                <th scope="col" className="client-detail-drawer__th">
                  Тип операции
                </th>
                <th scope="col" className="client-detail-drawer__th client-detail-drawer__th--sum">
                  Сумма
                </th>
                <th scope="col" className="client-detail-drawer__th client-detail-drawer__th--op-actions">
                  <span className="client-detail-drawer__sr-only">Действия</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleOperationHistoryRows.map((r, idx) => (
                <tr key={`op-row-${r.key}-${idx}`}>
                  <td className="client-detail-drawer__td client-detail-drawer__td--op-date-cell">{r.date}</td>
                  <td className="client-detail-drawer__td client-detail-drawer__td--op-type">
                    <div className="client-detail-drawer__op-type-cell">
                      <span className={r.opBadgeCls}>{r.typeLabel}</span>
                      {r.isRefunded && r.refundDoneLabel ? (
                        <span
                          className="client-detail-drawer__op-badge client-detail-drawer__op-badge--grey"
                          title={r.refundDoneLabel}
                        >
                          {r.refundDoneLabel}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="client-detail-drawer__td client-detail-drawer__td--right">
                    <span
                      className={`client-detail-drawer__op-sum${r.isRefunded ? ' client-detail-drawer__op-sum--refunded' : ''}`}
                    >
                      {r.amountFormatted}
                    </span>
                  </td>
                  <td className="client-detail-drawer__td client-detail-drawer__td--op-actions">
                    {r.canRefund ? (
                      <button
                        type="button"
                        className="client-detail-drawer__op-refund-btn"
                        aria-label={
                          r.refundProvider === 'cabinet_balance'
                            ? `Возврат ${r.refundAmountFormatted} на баланс ЛК`
                            : `Возврат ${r.refundAmountFormatted} через АТОЛ`
                        }
                        disabled={cabinetBillingOrdersLoading || refundingOrderId === r.orderId}
                        onClick={() => handleOperationRefundClick(r)}
                      >
                        {refundingOrderId === r.orderId ? '…' : 'Возврат'}
                      </button>
                    ) : r.isRefunded && r.refundDoneLabel ? (
                      <span
                        className="client-detail-drawer__op-badge client-detail-drawer__op-badge--grey client-detail-drawer__op-refund-done"
                        role="status"
                        aria-label={`Выполнен возврат: ${r.refundDoneLabel}`}
                      >
                        {r.refundDoneLabel}
                      </span>
                    ) : (
                      <span className="client-detail-drawer__op-actions-empty" aria-hidden>
                        —
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="client-detail-drawer__orders-foot client-detail-drawer__operations-foot">
          <span className="client-detail-drawer__orders-foot-range">
            {operationsHistoryCount === 0
              ? '0'
              : `${cabinetHistoryRangeStart}-${cabinetHistoryRangeEnd}`}{' '}
            из {operationsHistoryCount} записей
          </span>
          <div className="client-detail-drawer__pagination" role="navigation" aria-label="Страницы операций">
            <button
              type="button"
              className={`client-detail-drawer__page-icon${cabinetHistoryFirstDisabled ? ' client-detail-drawer__page-icon--muted' : ''}`}
              aria-label="Первая страница"
              disabled={cabinetHistoryFirstDisabled}
              onClick={handleCabinetHistoryFirst}
            >
              <ChevronDoubleLeft />
            </button>
            <button
              type="button"
              className={`client-detail-drawer__page-icon${cabinetHistoryPrevDisabled ? ' client-detail-drawer__page-icon--muted' : ''}`}
              aria-label="Предыдущая страница"
              disabled={cabinetHistoryPrevDisabled}
              onClick={handleCabinetHistoryPrev}
            >
              <ChevronLeft />
            </button>
            {Array.from({ length: cabinetHistoryTotalPages }, (_, i) => {
              const page = i + 1;
              const isActive = page === cabinetHistoryCurrentPage;
              return (
                <button
                  key={page}
                  type="button"
                  className={isActive ? 'client-detail-drawer__page-current' : 'client-detail-drawer__page-num'}
                  aria-label={`Страница ${page}`}
                  aria-current={isActive ? 'page' : undefined}
                  disabled={isActive || cabinetBillingOrdersLoading || operationsHistoryCount === 0}
                  onClick={() => setCabinetHistoryPage(page)}
                >
                  {page}
                </button>
              );
            })}
            <button
              type="button"
              className={`client-detail-drawer__page-icon${cabinetHistoryNextDisabled ? ' client-detail-drawer__page-icon--muted' : ''}`}
              aria-label="Следующая страница"
              disabled={cabinetHistoryNextDisabled}
              onClick={handleCabinetHistoryNext}
            >
              <ChevronRight />
            </button>
            <button
              type="button"
              className={`client-detail-drawer__page-icon${cabinetHistoryLastDisabled ? ' client-detail-drawer__page-icon--muted' : ''}`}
              aria-label="Последняя страница"
              disabled={cabinetHistoryLastDisabled}
              onClick={handleCabinetHistoryLast}
            >
              <ChevronDoubleRight />
            </button>
          </div>
        </div>
      </>
    </Spin>
  );

  return (
    <>
    <Drawer
      rootClassName={`client-detail-drawer${isMobileLayout ? ' client-detail-drawer--mobile' : ''}`}
      placement="right"
      width={drawerWidth}
      open={open}
      onClose={handleDrawerClose}
      closable={false}
      destroyOnClose
      styles={{ body: { padding: 0 } }}
      aria-labelledby="client-detail-drawer-title"
    >
      {client ? (
        <div className="client-detail-drawer__inner">
          <header className="client-detail-drawer__head">
            <div className="client-detail-drawer__head-actions">
              <button
                type="button"
                className="client-detail-drawer__close-btn"
                onClick={handleDrawerClose}
                aria-label="Закрыть панель"
                tabIndex={0}
              >
                <CloseOutlined />
              </button>
            </div>
            <div className="client-detail-drawer__head-left">
              <h2 id="client-detail-drawer-title" className="client-detail-drawer__title">
                {getDisplayTitle(client)}
              </h2>
              <span className="client-detail-drawer__head-meta" title="Совпадает с client_id в JWT личного кабинета">
                id {client.id}
                {client.cabinet_login ? ` · ЛК: ${client.cabinet_login}` : ''}
              </span>
              <div className="client-detail-drawer__chips-row" role="group" aria-label="Баланс и траты ЛК">
                <div className="client-detail-drawer__chip-balance">
                  <span className="client-detail-drawer__chip-balance-label">Баланс:</span>
                  <span className="client-detail-drawer__chip-balance-value">{balanceHeader}</span>
                </div>
                <div
                  className="client-detail-drawer__chip-balance client-detail-drawer__chip-balance--spend"
                  title="Оплаченные покупки ЛК в ₽ (те же правила, что для уровня привилегий), за всё время"
                >
                  <span className="client-detail-drawer__chip-balance-label">Траты (всего):</span>
                  <span className="client-detail-drawer__chip-balance-value">{privilegeSpendChips.lifetime}</span>
                </div>
                <div
                  className="client-detail-drawer__chip-balance client-detail-drawer__chip-balance--spend"
                  title="Оплаченные покупки ЛК в ₽ за последние 30 суток (те же правила, что для уровня привилегий)"
                >
                  <span className="client-detail-drawer__chip-balance-label">Траты (30 дн.):</span>
                  <span className="client-detail-drawer__chip-balance-value">{privilegeSpendChips.month}</span>
                </div>
              </div>
              <div
                className={`client-detail-drawer__level-pill${showBronzeWordmark ? ' client-detail-drawer__level-pill--svg-badge' : ''}${showGoldWordmark ? ' client-detail-drawer__level-pill--gold-badge' : ''}`}
                role="img"
                aria-label={`Уровень: ${levelLabel}`}
              >
                {showBronzeWordmark ? (
                  <BronzeLevelHeaderBadge className="client-detail-drawer__bronze-badge-svg" />
                ) : showGoldWordmark ? (
                  <>
                    <span className="client-detail-drawer__level-pill-crown" aria-hidden>
                      <CrownIcon gradientIdPrefix="r7" colorTint="#c1a875" className="settings-loyalty-card__crown-svg" />
                    </span>
                    <img
                      src={goldLevelHeaderWordmark}
                      className="client-detail-drawer__level-pill-title-svg client-detail-drawer__level-pill-title-svg--gold"
                      width="51"
                      height="11"
                      alt=""
                      aria-hidden
                      decoding="async"
                    />
                  </>
                ) : showSilverWordmark ? (
                  <>
                    <span className="client-detail-drawer__level-pill-crown" aria-hidden>
                      <CrownIcon gradientIdPrefix="r7" colorTint="#d6d3d1" className="settings-loyalty-card__crown-svg" />
                    </span>
                    <img
                      src={silverLevelHeaderWordmark}
                      className="client-detail-drawer__level-pill-title-svg client-detail-drawer__level-pill-title-svg--silver"
                      width="64"
                      height="14"
                      alt=""
                      aria-hidden
                      decoding="async"
                    />
                  </>
                ) : (
                  <>
                    <CrownIcon />
                    <span>{levelLabel}</span>
                  </>
                )}
              </div>
            </div>
          </header>

          <div
            className={`client-detail-drawer__body${tab === 'profile' && !isMobileLayout ? ' client-detail-drawer__body--profile-desktop' : ''}`}
          >
            <div className="client-detail-drawer__toolbar">
              {isMobileLayout ? (
                <div className="client-detail-drawer__tabs client-detail-drawer__tabs--mobile" role="tablist" aria-label="Разделы клиента">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'info' && infoView === 'summary'}
                    className={`client-detail-drawer__tab${tab === 'info' && infoView === 'summary' ? ' client-detail-drawer__tab--active' : ' client-detail-drawer__tab--idle'}`}
                    onClick={handleMobileSummary}
                  >
                    Сводка по группам
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'profile'}
                    className={`client-detail-drawer__tab${tab === 'profile' ? ' client-detail-drawer__tab--active' : ' client-detail-drawer__tab--idle'}`}
                    onClick={() => setTab('profile')}
                  >
                    Профиль
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'logs'}
                    className={`client-detail-drawer__tab${tab === 'logs' ? ' client-detail-drawer__tab--active' : ' client-detail-drawer__tab--idle'}`}
                    onClick={() => setTab('logs')}
                  >
                    Логи
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'info' && infoView === 'orders'}
                    className={`client-detail-drawer__tab${tab === 'info' && infoView === 'orders' ? ' client-detail-drawer__tab--active' : ' client-detail-drawer__tab--idle'}`}
                    onClick={handleMobileOrders}
                  >
                    <span className="client-detail-drawer__tab-label">Заказы</span>
                    <span className="client-detail-drawer__tab-num">{ordersHistoryCount}</span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'info' && infoView === 'operations'}
                    className={`client-detail-drawer__tab${tab === 'info' && infoView === 'operations' ? ' client-detail-drawer__tab--active' : ' client-detail-drawer__tab--idle'}`}
                    onClick={handleMobileOperations}
                  >
                    <span className="client-detail-drawer__tab-label">Операции</span>
                    <span className="client-detail-drawer__tab-num">{operationsHistoryCount}</span>
                  </button>
                </div>
              ) : (
                <div className="client-detail-drawer__tabs" role="tablist" aria-label="Разделы клиента">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'info'}
                    className={`client-detail-drawer__tab${tab === 'info' ? ' client-detail-drawer__tab--active' : ' client-detail-drawer__tab--idle'}`}
                    onClick={handleTabInfo}
                  >
                    Общая информация
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'profile'}
                    className={`client-detail-drawer__tab${tab === 'profile' ? ' client-detail-drawer__tab--active' : ' client-detail-drawer__tab--idle'}`}
                    onClick={() => setTab('profile')}
                  >
                    Профиль
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'logs'}
                    className={`client-detail-drawer__tab${tab === 'logs' ? ' client-detail-drawer__tab--active' : ' client-detail-drawer__tab--idle'}`}
                    onClick={() => setTab('logs')}
                  >
                    Логи
                  </button>
                </div>
              )}
              <div className="client-detail-drawer__toolbar-actions">
                <Tooltip title="Если включено, при создании и миграции прокси клиентом список запрещенных ресурсов не используется">
                  <label
                    className="client-detail-drawer__skip-forbidden"
                    htmlFor={skipForbiddenSwitchId}
                  >
                    <span className="client-detail-drawer__skip-forbidden-label">
                      Не использовать запреты
                    </span>
                    <Switch
                      id={skipForbiddenSwitchId}
                      size="small"
                      checked={Boolean(client?.skip_forbidden_resources)}
                      loading={skipForbiddenSaving}
                      disabled={!client?.id || skipForbiddenSaving}
                      onChange={handleSkipForbiddenResourcesChange}
                      aria-label="Не использовать запреты"
                      tabIndex={0}
                    />
                  </label>
                </Tooltip>
                {tab === 'logs' && activeActivityLogFiltersCount > 0 ? (
                  <Tooltip title={`Сбросить фильтры (${activeActivityLogFiltersCount})`}>
                    <button
                      type="button"
                      className="client-detail-drawer__logs-clear-filters-btn"
                      onClick={handleClearAllActivityLogFilters}
                      aria-label={`Сбросить фильтры (${activeActivityLogFiltersCount})`}
                      tabIndex={0}
                    >
                      <DrawerLogsClearFiltersIcon />
                    </button>
                  </Tooltip>
                ) : null}
                <button
                  type="button"
                  className="client-detail-drawer__toolbar-refresh"
                  onClick={handleRefresh}
                  aria-label="Обновить"
                  tabIndex={0}
                >
                  <ToolbarSyncIcon />
                </button>
                {tab === 'logs' || tab === 'profile' ? (
                  <button
                    type="button"
                    className="client-detail-drawer__toolbar-refresh"
                    onClick={tab === 'logs' ? handleExportLogs : handleExportProfile}
                    aria-label={tab === 'logs' ? 'Выгрузить журнал' : 'Выгрузить данные профиля'}
                    tabIndex={0}
                  >
                    <ToolbarDownloadGlyph />
                  </button>
                ) : null}
              </div>
            </div>

            <div className="client-detail-drawer__body-scroll">
              {tab === 'logs' ? (
                <div className="client-detail-drawer__logs-panel" role="region" aria-label="Журнал событий по клиенту">
                  <div className="client-detail-drawer__logs-scroll">
                    <Spin spinning={activityLogsLoading} aria-label="Загрузка журнала">
                      <div className="client-detail-drawer__history-table-wrap client-detail-drawer__logs-table-wrap">
                        <table className="client-detail-drawer__table client-detail-drawer__table--logs" aria-label="Логи">
                          <colgroup>
                            <col className="client-detail-drawer__table-col--log-time" />
                            <col className="client-detail-drawer__table-col--log-level" />
                            <col className="client-detail-drawer__table-col--log-cat" />
                            <col className="client-detail-drawer__table-col--log-action" />
                            <col className="client-detail-drawer__table-col--log-msg" />
                            <col className="client-detail-drawer__table-col--log-user" />
                          </colgroup>
                          <thead>
                            <tr>
                              <th className="client-detail-drawer__th client-detail-drawer__th--logs-head" scope="col">
                                <LogColumnHeader
                                  label="Время"
                                  filterLabel="Время"
                                  filtered={
                                    Boolean(
                                      String(activityLogFilters.dateFrom || '').trim() ||
                                        String(activityLogFilters.dateTo || '').trim(),
                                    )
                                  }
                                  onPopoverOpenChange={handleActivityLogTimePopoverOpenChange}
                                  filterContent={
                                    <Space direction="vertical" size="small" className="client-detail-drawer__log-filter-popover-inner">
                                      <DatePicker.RangePicker
                                        value={activityLogDraftTime}
                                        onChange={setActivityLogDraftTime}
                                        allowEmpty={[true, true]}
                                      />
                                      <Space>
                                        <Button type="primary" size="small" onClick={handleApplyActivityLogTimeFilter}>
                                          Применить
                                        </Button>
                                        <Button size="small" onClick={handleClearActivityLogTimeFilter}>
                                          Сбросить
                                        </Button>
                                      </Space>
                                    </Space>
                                  }
                                />
                              </th>
                              <th className="client-detail-drawer__th client-detail-drawer__th--logs-head" scope="col">
                                <LogColumnHeader
                                  label="Уровень"
                                  filterLabel="Уровень"
                                  filtered={Boolean(String(activityLogFilters.level || '').trim())}
                                  onPopoverOpenChange={handleActivityLogLevelPopoverOpenChange}
                                  filterContent={
                                    <Space direction="vertical" size="small" className="client-detail-drawer__log-filter-popover-inner">
                                      <Select
                                        value={activityLogDraftLevel === '' ? '' : activityLogDraftLevel}
                                        onChange={(v) => setActivityLogDraftLevel(v ?? '')}
                                        options={LOG_FILTER_LEVEL_OPTS}
                                        style={{ minWidth: 200 }}
                                      />
                                      <Space>
                                        <Button type="primary" size="small" onClick={handleApplyActivityLogLevelFilter}>
                                          Применить
                                        </Button>
                                        <Button size="small" onClick={handleClearActivityLogLevelFilter}>
                                          Сбросить
                                        </Button>
                                      </Space>
                                    </Space>
                                  }
                                />
                              </th>
                              <th className="client-detail-drawer__th client-detail-drawer__th--logs-head" scope="col">
                                <LogColumnHeader
                                  label="Категория"
                                  filterLabel="Категория"
                                  filtered={Boolean(String(activityLogFilters.category || '').trim())}
                                  onPopoverOpenChange={handleActivityLogCategoryPopoverOpenChange}
                                  filterContent={
                                    <Space direction="vertical" size="small" className="client-detail-drawer__log-filter-popover-inner">
                                      <Select
                                        value={activityLogDraftCategory === '' ? '' : activityLogDraftCategory}
                                        onChange={(v) => setActivityLogDraftCategory(v ?? '')}
                                        options={LOG_FILTER_CATEGORY_OPTS}
                                        style={{ minWidth: 200 }}
                                      />
                                      <Space>
                                        <Button type="primary" size="small" onClick={handleApplyActivityLogCategoryFilter}>
                                          Применить
                                        </Button>
                                        <Button size="small" onClick={handleClearActivityLogCategoryFilter}>
                                          Сбросить
                                        </Button>
                                      </Space>
                                    </Space>
                                  }
                                />
                              </th>
                              <th className="client-detail-drawer__th client-detail-drawer__th--logs-head" scope="col">
                                <LogColumnHeader
                                  label="Действие"
                                  filterLabel="Действие"
                                  filtered={Boolean(String(activityLogFilters.action || '').trim())}
                                  onPopoverOpenChange={handleActivityLogActionPopoverOpenChange}
                                  filterContent={
                                    <Space direction="vertical" size="small" className="client-detail-drawer__log-filter-popover-inner">
                                      <Input
                                        value={activityLogDraftAction}
                                        onChange={(e) => setActivityLogDraftAction(e.target.value)}
                                        placeholder="Содержит…"
                                        allowClear
                                      />
                                      <Space>
                                        <Button type="primary" size="small" onClick={handleApplyActivityLogActionFilter}>
                                          Применить
                                        </Button>
                                        <Button size="small" onClick={handleClearActivityLogActionFilter}>
                                          Сбросить
                                        </Button>
                                      </Space>
                                    </Space>
                                  }
                                />
                              </th>
                              <th className="client-detail-drawer__th client-detail-drawer__th--logs-head" scope="col">
                                <LogColumnHeader
                                  label="Сообщение"
                                  filterLabel="Сообщение"
                                  filtered={Boolean(String(activityLogFilters.message || '').trim())}
                                  onPopoverOpenChange={handleActivityLogMessagePopoverOpenChange}
                                  filterContent={
                                    <Space direction="vertical" size="small" className="client-detail-drawer__log-filter-popover-inner">
                                      <Input
                                        value={activityLogDraftMessage}
                                        onChange={(e) => setActivityLogDraftMessage(e.target.value)}
                                        placeholder="Содержит…"
                                        allowClear
                                      />
                                      <Space>
                                        <Button type="primary" size="small" onClick={handleApplyActivityLogMessageFilter}>
                                          Применить
                                        </Button>
                                        <Button size="small" onClick={handleClearActivityLogMessageFilter}>
                                          Сбросить
                                        </Button>
                                      </Space>
                                    </Space>
                                  }
                                />
                              </th>
                              <th className="client-detail-drawer__th client-detail-drawer__th--logs-head" scope="col">
                                <LogColumnHeader
                                  label="Пользователь"
                                  filterLabel="Пользователь"
                                  filtered={Boolean(String(activityLogFilters.user || '').trim())}
                                  onPopoverOpenChange={handleActivityLogUserPopoverOpenChange}
                                  filterContent={
                                    <Space direction="vertical" size="small" className="client-detail-drawer__log-filter-popover-inner">
                                      <Input
                                        value={activityLogDraftUser}
                                        onChange={(e) => setActivityLogDraftUser(e.target.value)}
                                        placeholder="Логин содержит…"
                                        allowClear
                                      />
                                      <Space>
                                        <Button type="primary" size="small" onClick={handleApplyActivityLogUserFilter}>
                                          Применить
                                        </Button>
                                        <Button size="small" onClick={handleClearActivityLogUserFilter}>
                                          Сбросить
                                        </Button>
                                      </Space>
                                    </Space>
                                  }
                                />
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {activityLogs.map((row) => (
                              <tr key={row.id}>
                                <td className="client-detail-drawer__td client-detail-drawer__td--log">{row.time}</td>
                                <td className="client-detail-drawer__td client-detail-drawer__td--log">{renderLogLevelBadge(row.level)}</td>
                                <td className="client-detail-drawer__td client-detail-drawer__td--log">{renderLogCategoryBadge(row.category)}</td>
                                <td className="client-detail-drawer__td client-detail-drawer__td--log">{row.action}</td>
                                <td className="client-detail-drawer__td client-detail-drawer__td--log client-detail-drawer__td--log-msg">
                                  {row.message}
                                </td>
                                <td className="client-detail-drawer__td client-detail-drawer__td--log">
                                  {row.user != null && row.user !== '' ? row.user : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {!activityLogsLoading && activityLogsTotal === 0 ? (
                        <p className="client-detail-drawer__logs-empty">
                          Нет записей в журнале
                        </p>
                      ) : null}
                    </Spin>
                  </div>
                  <div className="client-detail-drawer__logs-footer">
                    <span className="client-detail-drawer__logs-footer-range">
                      <span className="client-detail-drawer__logs-footer-num">{logRangeStart === 0 ? '0' : `${logRangeStart}-${logRangeEnd}`}</span>
                      <span className="client-detail-drawer__logs-footer-muted"> из </span>
                      <span className="client-detail-drawer__logs-footer-num">{logTotal}</span>
                      <span className="client-detail-drawer__logs-footer-muted"> записей</span>
                    </span>
                    <div className="client-detail-drawer__pagination client-detail-drawer__pagination--logs">
                      <button
                        type="button"
                        className="client-detail-drawer__page-btn"
                        aria-label="Первая страница"
                        disabled={logsPrevDisabled}
                        onClick={handleLogsFirst}
                      >
                        <ChevronDoubleLeft />
                      </button>
                      <button
                        type="button"
                        className="client-detail-drawer__page-btn"
                        aria-label="Предыдущая страница"
                        disabled={logsPrevDisabled}
                        onClick={handleLogsPrev}
                      >
                        <ChevronLeft />
                      </button>
                      <span className="client-detail-drawer__page-current" aria-current="page">
                        {logsCurrentPage}
                      </span>
                      <button
                        type="button"
                        className="client-detail-drawer__page-btn"
                        aria-label="Следующая страница"
                        disabled={logsNextDisabled}
                        onClick={handleLogsNext}
                      >
                        <ChevronRight />
                      </button>
                      <button
                        type="button"
                        className="client-detail-drawer__page-btn"
                        aria-label="Последняя страница"
                        disabled={logsNextDisabled}
                        onClick={handleLogsLast}
                      >
                        <ChevronDoubleRight />
                      </button>
                    </div>
                    <div className="client-detail-drawer__logs-per-page" role="group" aria-label="Записей на странице">
                      <span className="client-detail-drawer__logs-per-page-text">
                        <span className="client-detail-drawer__logs-per-page-val">{LOGS_PAGE_SIZE}</span>
                        <span className="client-detail-drawer__logs-per-page-muted"> на странице</span>
                      </span>
                      <svg className="client-detail-drawer__logs-per-page-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                        <path d="M4 6L8 10L12 6" stroke="#7a7a7a" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  </div>
                </div>
              ) : null}

              {tab === 'profile' ? (
                isMobileLayout ? (
                  <div className="client-detail-drawer__profile-stack">
                    <section className="client-detail-drawer__card client-detail-drawer__card--rounded-lg">
                      <div className="client-detail-drawer__card-head">
                        <h3 className="client-detail-drawer__card-title">Данные пользователя</h3>
                      </div>
                      <div className="client-detail-drawer__card-body">
                        {profileUserFields.map((row) => (
                          <div key={row.id} className="client-detail-drawer__field">
                            <label className="client-detail-drawer__field-label" htmlFor={row.id}>
                              {row.label}
                            </label>
                            <input
                              id={row.id}
                              type="text"
                              readOnly
                              className={`client-detail-drawer__field-input${row.accent ? ' client-detail-drawer__field-input--accent' : ''}`}
                              value={row.value}
                              tabIndex={0}
                            />
                          </div>
                        ))}
                        {renderProxyPasswordRevealField()}
                      </div>
                    </section>

                    <section className="client-detail-drawer__card client-detail-drawer__card--rounded-lg">
                      <div className="client-detail-drawer__card-head">
                        <h3 className="client-detail-drawer__card-title">Баланс и уровень</h3>
                      </div>
                      <div className="client-detail-drawer__card-body">
                        <div className="client-detail-drawer__field">
                          <label className="client-detail-drawer__field-label" htmlFor="cd-balance">
                            Зачислить на баланс
                          </label>
                          <input
                            id="cd-balance"
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            className="client-detail-drawer__field-input"
                            value={cabinetBalanceDraft}
                            onChange={(e) => setCabinetBalanceDraft(e.target.value)}
                            aria-label="Сумма зачисления на баланс личного кабинета, ₽"
                            tabIndex={0}
                          />
                        </div>
                        <div className="client-detail-drawer__field">
                          <label className="client-detail-drawer__field-label" htmlFor="cd-level">
                            Уровень привилегий (ЛК)
                          </label>
                          <select
                            id="cd-level"
                            className="client-detail-drawer__field-input client-detail-drawer__field-input--select"
                            value={privilegeTierDraft}
                            onChange={(e) => setPrivilegeTierDraft(e.target.value)}
                            aria-label="Назначение уровня привилегий для личного кабинета"
                            tabIndex={0}
                          >
                            {PRIVILEGE_TIER_SELECT_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="client-detail-drawer__field">
                          <label className="client-detail-drawer__field-label" htmlFor="cd-discount">
                            Персональная скидка, %
                          </label>
                          <input
                            id="cd-discount"
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            className="client-detail-drawer__field-input"
                            value={personalDiscountDraft}
                            onChange={(e) => setPersonalDiscountDraft(e.target.value)}
                            aria-label="Персональная скидка клиента, %"
                            tabIndex={0}
                          />
                        </div>
                        <div className="client-detail-drawer__balance-save-row">
                          <button
                            type="button"
                            className="client-detail-drawer__btn-outline"
                            onClick={handleSaveCabinetProfile}
                            disabled={cabinetProfileSaving}
                            aria-label="Сохранить баланс и уровень привилегий личного кабинета"
                            tabIndex={0}
                          >
                            {cabinetProfileSaving ? 'Сохранение…' : 'Сохранить'}
                          </button>
                        </div>
                      </div>
                    </section>

                    <section className="client-detail-drawer__card client-detail-drawer__card--rounded-lg">
                      <div className="client-detail-drawer__card-head">
                        <h3 className="client-detail-drawer__card-title">Смена пароля</h3>
                      </div>
                      <div className="client-detail-drawer__card-body">
                        <div className="client-detail-drawer__hint">
                          Пароль должен быть не короче 8 символов, содержать буквы и цифры.
                        </div>
                        <div className="client-detail-drawer__field">
                          <label className="client-detail-drawer__field-label" htmlFor="cd-pw1">
                            Новый пароль
                          </label>
                          <div className="client-detail-drawer__password-input-wrap">
                            <input
                              id="cd-pw1"
                              type={showCabinetNewPassword ? 'text' : 'password'}
                              autoComplete="new-password"
                              className="client-detail-drawer__field-input client-detail-drawer__field-input--with-toggle"
                              placeholder="Введите новый пароль"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              aria-label="Новый пароль для входа в личный кабинет и прокси"
                              tabIndex={0}
                            />
                            <button
                              type="button"
                              className="client-detail-drawer__password-toggle"
                              onClick={() => setShowCabinetNewPassword((v) => !v)}
                              aria-label={showCabinetNewPassword ? 'Скрыть пароль' : 'Показать пароль'}
                              aria-pressed={showCabinetNewPassword}
                              tabIndex={0}
                            >
                              {showCabinetNewPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                            </button>
                          </div>
                        </div>
                        <div className="client-detail-drawer__field">
                          <label className="client-detail-drawer__field-label" htmlFor="cd-pw2">
                            Повторите новый пароль
                          </label>
                          <div className="client-detail-drawer__password-input-wrap">
                            <input
                              id="cd-pw2"
                              type={showCabinetRepeatPassword ? 'text' : 'password'}
                              autoComplete="new-password"
                              className="client-detail-drawer__field-input client-detail-drawer__field-input--with-toggle"
                              placeholder="Повторите новый пароль"
                              value={repeatPassword}
                              onChange={(e) => setRepeatPassword(e.target.value)}
                              aria-label="Повтор нового пароля"
                              tabIndex={0}
                            />
                            <button
                              type="button"
                              className="client-detail-drawer__password-toggle"
                              onClick={() => setShowCabinetRepeatPassword((v) => !v)}
                              aria-label={showCabinetRepeatPassword ? 'Скрыть пароль' : 'Показать пароль'}
                              aria-pressed={showCabinetRepeatPassword}
                              tabIndex={0}
                            >
                              {showCabinetRepeatPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                            </button>
                          </div>
                        </div>
                        <div className="client-detail-drawer__password-actions-row">
                          <button
                            type="button"
                            className="client-detail-drawer__btn-primary client-detail-drawer__btn-primary--save-profile"
                            onClick={handleSaveCabinetPassword}
                            disabled={cabinetPasswordSaving}
                            aria-label="Сохранить новый пароль клиента для личного кабинета"
                            tabIndex={0}
                          >
                            {cabinetPasswordSaving ? 'Сохранение…' : 'Сохранить пароль'}
                          </button>
                          <button
                            type="button"
                            className="client-detail-drawer__btn-outline"
                            onClick={handleGeneratePassword}
                            disabled={cabinetPasswordSaving}
                            aria-label="Сгенерировать случайный пароль"
                            tabIndex={0}
                          >
                            Сгенерировать
                          </button>
                        </div>
                      </div>
                    </section>

                    <section className="client-detail-drawer__card client-detail-drawer__card--rounded-lg client-detail-drawer__tg">
                      <div className="client-detail-drawer__card-head">
                        <h3 className="client-detail-drawer__card-title">Уведомления в Телеграмм</h3>
                      </div>
                      <div className="client-detail-drawer__card-body">
                        <label className="client-detail-drawer__tg-row">
                          <input
                            type="checkbox"
                            className="client-detail-drawer__checkbox client-detail-drawer__checkbox--native"
                            checked={tgProxy}
                            onChange={(e) => setTgProxy(e.target.checked)}
                          />
                          <span className="client-detail-drawer__checkbox-visual" aria-hidden>
                            <TgCheckboxIcon checked={tgProxy} />
                          </span>
                          <span className="client-detail-drawer__tg-label">Уведомления о недоступности прокси</span>
                        </label>
                        <label className="client-detail-drawer__tg-row">
                          <input
                            type="checkbox"
                            className="client-detail-drawer__checkbox client-detail-drawer__checkbox--native"
                            checked={tgServer}
                            onChange={(e) => setTgServer(e.target.checked)}
                          />
                          <span className="client-detail-drawer__checkbox-visual" aria-hidden>
                            <TgCheckboxIcon checked={tgServer} />
                          </span>
                          <span className="client-detail-drawer__tg-label">Уведомления о недоступности серверов</span>
                        </label>
                        <label className="client-detail-drawer__tg-row">
                          <input
                            type="checkbox"
                            className="client-detail-drawer__checkbox client-detail-drawer__checkbox--native"
                            checked={tgRenew}
                            onChange={(e) => setTgRenew(e.target.checked)}
                          />
                          <span className="client-detail-drawer__checkbox-visual" aria-hidden>
                            <TgCheckboxIcon checked={tgRenew} />
                          </span>
                          <span className="client-detail-drawer__tg-label">Уведомления о необходимости продления прокси</span>
                        </label>
                      </div>
                    </section>
                  </div>
                ) : (
                  <div className="client-detail-drawer__profile-stack client-detail-drawer__profile-stack--desktop">
                    <div className="client-detail-drawer__profile-desktop-cols">
                      <section className="client-detail-drawer__card client-detail-drawer__card--profile-mock">
                        <div className="client-detail-drawer__card-head client-detail-drawer__card-head--profile-mock">
                          <h3 className="client-detail-drawer__card-title client-detail-drawer__card-title--profile-mock">Данные пользователя</h3>
                        </div>
                        <div className="client-detail-drawer__card-body client-detail-drawer__card-body--profile-mock">
                          {profileUserFields.map((row) => (
                            <div key={row.id} className="client-detail-drawer__field client-detail-drawer__field--profile-mock">
                              <label className="client-detail-drawer__field-label" htmlFor={`${row.id}-desk`}>
                                {row.label}
                              </label>
                              <input
                                id={`${row.id}-desk`}
                                type="text"
                                readOnly
                                className={`client-detail-drawer__field-input client-detail-drawer__field-input--profile-mock${row.accent ? ' client-detail-drawer__field-input--accent' : ''}`}
                                value={row.value}
                                tabIndex={0}
                              />
                            </div>
                          ))}
                          {renderProxyPasswordRevealField({
                            idSuffix: '-desk',
                            fieldClassName: 'client-detail-drawer__field--profile-mock',
                            inputClassName: 'client-detail-drawer__field-input--profile-mock',
                            toggleClassName: 'client-detail-drawer__password-toggle--mock',
                          })}
                        </div>
                        <div className="client-detail-drawer__card-foot">
                          <button
                            type="button"
                            className="client-detail-drawer__btn-primary client-detail-drawer__btn-primary--save-profile"
                            onClick={handleSaveProfile}
                            tabIndex={0}
                          >
                            Сохранить
                          </button>
                        </div>
                      </section>

                      <div className="client-detail-drawer__profile-desktop-mid">
                        <section className="client-detail-drawer__card client-detail-drawer__card--profile-mock">
                          <div className="client-detail-drawer__card-head client-detail-drawer__card-head--profile-mock">
                            <h3 className="client-detail-drawer__card-title client-detail-drawer__card-title--profile-mock">Баланс и уровень</h3>
                          </div>
                          <div className="client-detail-drawer__card-body client-detail-drawer__card-body--profile-mock">
                            <div className="client-detail-drawer__field client-detail-drawer__field--profile-mock">
                              <label className="client-detail-drawer__field-label" htmlFor="cd-balance-desk">
                                Зачислить на баланс
                              </label>
                              <input
                                id="cd-balance-desk"
                                type="text"
                                inputMode="decimal"
                                autoComplete="off"
                                className="client-detail-drawer__field-input client-detail-drawer__field-input--profile-mock"
                                value={cabinetBalanceDraft}
                                onChange={(e) => setCabinetBalanceDraft(e.target.value)}
                                aria-label="Сумма зачисления на баланс личного кабинета, ₽"
                                tabIndex={0}
                              />
                            </div>
                            <div className="client-detail-drawer__field client-detail-drawer__field--profile-mock">
                              <label className="client-detail-drawer__field-label" htmlFor="cd-level-desk">
                                Уровень привилегий (ЛК)
                              </label>
                              <div className="client-detail-drawer__field-input-wrap">
                                <select
                                  id="cd-level-desk"
                                  className="client-detail-drawer__field-input client-detail-drawer__field-input--profile-mock client-detail-drawer__field-input--select"
                                  value={privilegeTierDraft}
                                  onChange={(e) => setPrivilegeTierDraft(e.target.value)}
                                  aria-label="Назначение уровня привилегий для личного кабинета"
                                  tabIndex={0}
                                >
                                  {PRIVILEGE_TIER_SELECT_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="client-detail-drawer__field client-detail-drawer__field--profile-mock">
                              <label className="client-detail-drawer__field-label" htmlFor="cd-discount-desk">
                                Персональная скидка, %
                              </label>
                              <input
                                id="cd-discount-desk"
                                type="text"
                                inputMode="decimal"
                                autoComplete="off"
                                className="client-detail-drawer__field-input client-detail-drawer__field-input--profile-mock"
                                value={personalDiscountDraft}
                                onChange={(e) => setPersonalDiscountDraft(e.target.value)}
                                aria-label="Персональная скидка клиента, %"
                                tabIndex={0}
                              />
                            </div>
                            <div className="client-detail-drawer__balance-save-row client-detail-drawer__balance-save-row--profile-mock">
                              <button
                                type="button"
                                className="client-detail-drawer__btn-primary client-detail-drawer__btn-primary--save-profile"
                                onClick={handleSaveCabinetProfile}
                                disabled={cabinetProfileSaving}
                                aria-label="Сохранить баланс и уровень привилегий личного кабинета"
                                tabIndex={0}
                              >
                                {cabinetProfileSaving ? 'Сохранение…' : 'Сохранить'}
                              </button>
                            </div>
                          </div>
                        </section>

                        <section className="client-detail-drawer__card client-detail-drawer__card--profile-mock">
                          <div className="client-detail-drawer__card-head client-detail-drawer__card-head--profile-mock">
                            <h3 className="client-detail-drawer__card-title client-detail-drawer__card-title--profile-mock">Смена пароля</h3>
                          </div>
                          <div className="client-detail-drawer__card-body client-detail-drawer__card-body--profile-mock client-detail-drawer__card-body--password-mock">
                          <div className="client-detail-drawer__hint client-detail-drawer__hint--password-mock">
                            Пароль должен быть не короче 8 символов, содержать буквы и цифры. Сохранение обновляет вход в личный кабинет и пароль прокси в Kraken.
                          </div>
                          <div className="client-detail-drawer__field client-detail-drawer__field--profile-mock">
                            <label className="client-detail-drawer__field-label" htmlFor="cd-pw1-desk">
                              Новый пароль
                            </label>
                            <div className="client-detail-drawer__password-input-wrap">
                              <input
                                id="cd-pw1-desk"
                                type={showCabinetNewPassword ? 'text' : 'password'}
                                autoComplete="new-password"
                                className="client-detail-drawer__field-input client-detail-drawer__field-input--profile-mock client-detail-drawer__field-input--placeholder-mock client-detail-drawer__field-input--with-toggle"
                                placeholder="Введите новый пароль"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                aria-label="Новый пароль для входа в личный кабинет и прокси"
                                tabIndex={0}
                              />
                              <button
                                type="button"
                                className="client-detail-drawer__password-toggle client-detail-drawer__password-toggle--mock"
                                onClick={() => setShowCabinetNewPassword((v) => !v)}
                                aria-label={showCabinetNewPassword ? 'Скрыть пароль' : 'Показать пароль'}
                                aria-pressed={showCabinetNewPassword}
                                tabIndex={0}
                              >
                                {showCabinetNewPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                              </button>
                            </div>
                          </div>
                          <div className="client-detail-drawer__field client-detail-drawer__field--profile-mock">
                            <label className="client-detail-drawer__field-label" htmlFor="cd-pw2-desk">
                              Повторите новый пароль
                            </label>
                            <div className="client-detail-drawer__password-input-wrap">
                              <input
                                id="cd-pw2-desk"
                                type={showCabinetRepeatPassword ? 'text' : 'password'}
                                autoComplete="new-password"
                                className="client-detail-drawer__field-input client-detail-drawer__field-input--profile-mock client-detail-drawer__field-input--placeholder-mock client-detail-drawer__field-input--with-toggle"
                                placeholder="Повторите новый пароль"
                                value={repeatPassword}
                                onChange={(e) => setRepeatPassword(e.target.value)}
                                aria-label="Повтор нового пароля"
                                tabIndex={0}
                              />
                              <button
                                type="button"
                                className="client-detail-drawer__password-toggle client-detail-drawer__password-toggle--mock"
                                onClick={() => setShowCabinetRepeatPassword((v) => !v)}
                                aria-label={showCabinetRepeatPassword ? 'Скрыть пароль' : 'Показать пароль'}
                                aria-pressed={showCabinetRepeatPassword}
                                tabIndex={0}
                              >
                                {showCabinetRepeatPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                              </button>
                            </div>
                          </div>
                          </div>
                          <div className="client-detail-drawer__card-foot client-detail-drawer__card-foot--password-mock">
                            <button
                              type="button"
                              className="client-detail-drawer__btn-primary client-detail-drawer__btn-primary--save-profile"
                              onClick={handleSaveCabinetPassword}
                              disabled={cabinetPasswordSaving}
                              aria-label="Сохранить новый пароль клиента для личного кабинета"
                              tabIndex={0}
                            >
                              {cabinetPasswordSaving ? 'Сохранение…' : 'Сохранить пароль'}
                            </button>
                            <button
                              type="button"
                              className="client-detail-drawer__btn-outline client-detail-drawer__btn-outline--profile-gen"
                              onClick={handleGeneratePassword}
                              disabled={cabinetPasswordSaving}
                              aria-label="Сгенерировать случайный пароль"
                              tabIndex={0}
                            >
                              Сгенерировать
                            </button>
                          </div>
                        </section>
                      </div>
                    </div>
                  </div>
                )
              ) : null}

              {tab === 'info' ? (
                <div className="client-detail-drawer__info-stack">
                  {(!isMobileLayout || infoView === 'summary') && (
                    <section className="client-detail-drawer__card client-detail-drawer__card--surface client-detail-drawer__card--summary-mobile">
                      <div className="client-detail-drawer__card-head client-detail-drawer__card-head--flex">
                        <h3 className="client-detail-drawer__card-title client-detail-drawer__card-title--summary">Сводка по группам</h3>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            className="client-detail-drawer__btn-primary client-detail-drawer__btn-primary--head"
                            tabIndex={0}
                            aria-label="Открыть журнал клиента из S3 в новой вкладке"
                            onClick={handleOpenJournalS3}
                          >
                            Журнал из S3
                            <ArrowLink />
                          </button>
                          <button
                            type="button"
                            className="client-detail-drawer__btn-primary client-detail-drawer__btn-primary--head"
                            tabIndex={0}
                            aria-label="Открыть раздел Прокси с фильтром по этому клиенту в новой вкладке"
                            onClick={handleNavigateToProxiesWithClientFilter}
                          >
                            К прокси
                            <ArrowLink />
                          </button>
                        </div>
                      </div>
                      <div
                        className={`client-detail-drawer__matrix${isMobileLayout ? ' client-detail-drawer__matrix--mobile-summary' : ''}`}
                        role="table"
                        aria-label="Сводка по группам"
                      >
                        <div className="client-detail-drawer__matrix-col client-detail-drawer__matrix-col--group">
                          <div className="client-detail-drawer__th" role="columnheader">
                            Группа
                          </div>
                          {summaryMatrixRows.map((r) => (
                            <div key={r.group} className="client-detail-drawer__td client-detail-drawer__td--neutral" role="cell">
                              {r.group}
                            </div>
                          ))}
                          {showSummaryMatrixTotals ? (
                            <div className="client-detail-drawer__td client-detail-drawer__td--total client-detail-drawer__td--neutral-bold" role="cell">
                              Всего
                            </div>
                          ) : null}
                        </div>
                        <div className="client-detail-drawer__matrix-col client-detail-drawer__matrix-col--w28">
                          <div className="client-detail-drawer__th" role="columnheader">
                            <span className="client-detail-drawer__th-text-full">Прокси</span>
                            <span className="client-detail-drawer__th-text-short" aria-hidden>
                              Про…
                            </span>
                          </div>
                          {summaryMatrixRows.map((r) => (
                            <div key={`p-${r.group}`} className="client-detail-drawer__td" role="cell">
                              {r._loading ? (
                                <span className="client-detail-drawer__cell-num client-detail-drawer__cell-num--muted">…</span>
                              ) : (
                                <span className="client-detail-drawer__cell-num client-detail-drawer__cell-num--proxy">{r.proxy}</span>
                              )}
                            </div>
                          ))}
                          {showSummaryMatrixTotals ? (
                            <div className="client-detail-drawer__td client-detail-drawer__td--total" role="cell">
                              <span className="client-detail-drawer__cell-num client-detail-drawer__cell-num--proxy">{groupTotals.proxy}</span>
                            </div>
                          ) : null}
                        </div>
                        <div className="client-detail-drawer__matrix-col client-detail-drawer__matrix-col--w28">
                          <div className="client-detail-drawer__th" role="columnheader">
                            <span className="client-detail-drawer__th-text-full">Активно</span>
                            <span className="client-detail-drawer__th-text-short" aria-hidden>
                              Акти…
                            </span>
                          </div>
                          {summaryMatrixRows.map((r) => (
                            <div key={`a-${r.group}`} className="client-detail-drawer__td" role="cell">
                              {r._loading ? (
                                <span className="client-detail-drawer__cell-num client-detail-drawer__cell-num--muted">…</span>
                              ) : (
                                renderNumTone(r.active, 'green')
                              )}
                            </div>
                          ))}
                          {showSummaryMatrixTotals ? (
                            <div className="client-detail-drawer__td client-detail-drawer__td--total" role="cell">
                              {renderNumTone(groupTotals.active, 'green')}
                            </div>
                          ) : null}
                        </div>
                        <div className="client-detail-drawer__matrix-col client-detail-drawer__matrix-col--w28">
                          <div className="client-detail-drawer__th" role="columnheader">
                            <span className="client-detail-drawer__th-text-full">Неактивно</span>
                            <span className="client-detail-drawer__th-text-short" aria-hidden>
                              Неакт…
                            </span>
                          </div>
                          {summaryMatrixRows.map((r) => (
                            <div key={`i-${r.group}`} className="client-detail-drawer__td" role="cell">
                              {r._loading ? (
                                <span className="client-detail-drawer__cell-num client-detail-drawer__cell-num--muted">…</span>
                              ) : (
                                renderNumTone(r.inactive)
                              )}
                            </div>
                          ))}
                          {showSummaryMatrixTotals ? (
                            <div className="client-detail-drawer__td client-detail-drawer__td--total" role="cell">
                              {renderNumTone(groupTotals.inactive)}
                            </div>
                          ) : null}
                        </div>
                        <div className="client-detail-drawer__matrix-col client-detail-drawer__matrix-col--w44">
                          <div className="client-detail-drawer__th" role="columnheader">
                            <span className="client-detail-drawer__th-text-full">Требуют продления</span>
                            <span className="client-detail-drawer__th-text-short" aria-hidden>
                              Треб…
                            </span>
                          </div>
                          {summaryMatrixRows.map((r) => (
                            <div key={`rn-${r.group}`} className="client-detail-drawer__td" role="cell">
                              {r._loading ? (
                                <span className="client-detail-drawer__cell-num client-detail-drawer__cell-num--muted">…</span>
                              ) : (
                                renderNumTone(r.renew)
                              )}
                            </div>
                          ))}
                          {showSummaryMatrixTotals ? (
                            <div className="client-detail-drawer__td client-detail-drawer__td--total" role="cell">
                              {renderNumTone(groupTotals.renew)}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      {isMobileLayout ? (
                        <div className="client-detail-drawer__summary-mobile-foot">
                          <button
                            type="button"
                            className="client-detail-drawer__btn-primary client-detail-drawer__btn-primary--foot"
                            tabIndex={0}
                            aria-label="Открыть раздел Прокси с фильтром по этому клиенту в новой вкладке"
                            onClick={handleNavigateToProxiesWithClientFilter}
                          >
                            К прокси
                            <ArrowLink />
                          </button>
                        </div>
                      ) : null}
                    </section>
                  )}

                  {!isMobileLayout && (
                    <section className="client-detail-drawer__card client-detail-drawer__card--surface client-detail-drawer__card--history">
                      <div className="client-detail-drawer__card-head client-detail-drawer__card-head--history">
                        <div className="client-detail-drawer__history-head-row">
                          <h3 className="client-detail-drawer__card-title client-detail-drawer__card-title--history">История</h3>
                          <div className="client-detail-drawer__history-chips" role="group" aria-label="Тип истории">
                            <button
                              type="button"
                              className={`client-detail-drawer__history-chip${historySubTab === 'orders' ? ' client-detail-drawer__history-chip--active' : ' client-detail-drawer__history-chip--idle'}`}
                              onClick={handleHistorySubTabOrders}
                              aria-pressed={historySubTab === 'orders'}
                            >
                              <span className="client-detail-drawer__history-chip-label">Заказы</span>
                              <span className="client-detail-drawer__history-chip-num">{ordersHistoryCount}</span>
                            </button>
                            <button
                              type="button"
                              className={`client-detail-drawer__history-chip${historySubTab === 'operations' ? ' client-detail-drawer__history-chip--active' : ' client-detail-drawer__history-chip--idle'}`}
                              onClick={handleHistorySubTabOperations}
                              aria-pressed={historySubTab === 'operations'}
                            >
                              <span className="client-detail-drawer__history-chip-label">Операции</span>
                              <span className="client-detail-drawer__history-chip-num">{operationsHistoryCount}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="client-detail-drawer__history-card-body">
                        {historySubTab === 'orders' ? renderOrdersTableBlock() : renderOperationsTableBlock()}
                      </div>
                    </section>
                  )}

                  {isMobileLayout && infoView === 'orders' && (
                    <section className="client-detail-drawer__card client-detail-drawer__card--surface client-detail-drawer__card--mobile-orders">
                      {renderOrdersTableBlock()}
                    </section>
                  )}

                  {isMobileLayout && infoView === 'operations' && (
                    <section className="client-detail-drawer__card client-detail-drawer__card--surface client-detail-drawer__card--mobile-operations">
                      {renderOperationsTableBlock()}
                    </section>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </Drawer>
    <Modal
      rootClassName="client-detail-drawer__order-modal-root"
      className="light-modal client-detail-drawer__order-modal"
      getContainer={() => document.body}
      title={
        cabinetOrderDetail?.order_number
          ? `Заказ ${cabinetOrderDetail.order_number}`
          : 'Заказ'
      }
      open={cabinetOrderDetailOpen}
      onCancel={handleCabinetOrderDetailModalClose}
      maskClosable={false}
      wrapProps={cabinetOrderDetailBackdrop}
      footer={null}
      width={720}
      destroyOnClose
      centered
      zIndex={10000}
    >
      {cabinetOrderDetailLoading ? (
        <div className="client-detail-drawer__order-modal-spin">
          <Spin />
        </div>
      ) : null}
      {!cabinetOrderDetailLoading && cabinetOrderDetailErr ? (
        <div className="client-detail-drawer__history-error" role="alert">
          {cabinetOrderDetailErr}
        </div>
      ) : null}
      {!cabinetOrderDetailLoading && !cabinetOrderDetailErr && cabinetOrderDetail ? (
        <div className="client-detail-drawer__order-detail">
          <div className="client-detail-drawer__order-detail-head">
            {renderCabinetPurchaseStatusBadge(cabinetPurchaseStatusPartsFromOrder(cabinetOrderDetail))}
          </div>
          <div className="client-detail-drawer__order-detail-grid">
            <p>
              <span className="client-detail-drawer__order-detail-k">Тип</span>
              <span className="client-detail-drawer__order-detail-v">{orderTypeLabelRu(cabinetOrderDetail.order_type)}</span>
            </p>
            <p>
              <span className="client-detail-drawer__order-detail-k">Оплата</span>
              <span className="client-detail-drawer__order-detail-v">
                {renderCabinetPaymentStatusBadgeDetail(cabinetOrderDetail.payment_status)}
              </span>
            </p>
            <p>
              <span className="client-detail-drawer__order-detail-k">Создан</span>
              <span className="client-detail-drawer__order-detail-v">
                {formatCabinetHistoryDateTime(cabinetOrderDetail.created_at)}
              </span>
            </p>
            <p>
              <span className="client-detail-drawer__order-detail-k">Сумма</span>
              <span className="client-detail-drawer__order-detail-v">
                {formatCabinetHistoryAmount(
                  cabinetOrderDetail.total_amount,
                  cabinetOrderDetail.currency === 'USD' ? 'USD' : 'RUB',
                )}
              </span>
            </p>
            {cabinetOrderDetail.flexible_tariff_name ? (
              <p>
                <span className="client-detail-drawer__order-detail-k">Тариф</span>
                <span className="client-detail-drawer__order-detail-v">
                  {renderTariffBadge(tariffTariffKindFromCabinetName(cabinetOrderDetail.flexible_tariff_name))}
                </span>
              </p>
            ) : null}
            <p>
              <span className="client-detail-drawer__order-detail-k">Прокси (заказано / активно / неактивно)</span>
              <span className="client-detail-drawer__order-detail-v">
                {cabinetOrderDetail.ordered_proxy_quantity ?? 0} / {cabinetOrderDetail.provisioned_active_count ?? 0} /{' '}
                {cabinetOrderDetail.provisioned_inactive_count ?? 0}
              </span>
            </p>
          </div>
          {(cabinetOrderDetail.provisioning_queue_status || cabinetOrderDetail.provisioning_last_error) ? (
            <div className="client-detail-drawer__order-detail-queue" role="status">
              {cabinetOrderDetail.provisioning_queue_status ? (
                <p>
                  <strong>Очередь выдачи:</strong> {cabinetOrderDetail.provisioning_queue_status}
                </p>
              ) : null}
              {cabinetOrderDetail.provisioning_last_error ? (
                <p>
                  <strong>Ошибка выдачи:</strong> {cabinetOrderDetail.provisioning_last_error}
                </p>
              ) : null}
            </div>
          ) : null}
          <h4 className="client-detail-drawer__order-detail-items-title">Позиции заказа</h4>
          <div className="client-detail-drawer__history-table-wrap client-detail-drawer__order-detail-items-wrap">
            <table className="client-detail-drawer__table client-detail-drawer__table--order-detail-items">
              <thead>
                <tr>
                  <th scope="col" className="client-detail-drawer__th">ID</th>
                  <th scope="col" className="client-detail-drawer__th">Кол-во</th>
                  <th scope="col" className="client-detail-drawer__th">Период (дней)</th>
                  <th scope="col" className="client-detail-drawer__th">Цена за ед.</th>
                  <th scope="col" className="client-detail-drawer__th">Итого</th>
                </tr>
              </thead>
              <tbody>
                {(cabinetOrderDetail.items || []).map((item) => (
                  <tr key={item.id}>
                    <td className="client-detail-drawer__td">{item.id}</td>
                    <td className="client-detail-drawer__td">{item.quantity}</td>
                    <td className="client-detail-drawer__td">{item.period_days}</td>
                    <td className="client-detail-drawer__td client-detail-drawer__td--right">
                      {formatCabinetHistoryAmount(
                        item.unit_price,
                        cabinetOrderDetail.currency === 'USD' ? 'USD' : 'RUB',
                      )}
                    </td>
                    <td className="client-detail-drawer__td client-detail-drawer__td--right">
                      {formatCabinetHistoryAmount(
                        item.total_price,
                        cabinetOrderDetail.currency === 'USD' ? 'USD' : 'RUB',
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </Modal>
    </>
  );
};

export default ClientDetailDrawer;
